import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_URL } from "../config";
import {
  IApiResponse,
  IApiResponseForPunch,
  IEmployeePunchImgPayload,
  ILoopingTableListPayload,
  IStudentListPayload,
  ISearchPayload,
  ITeamListPayload,
  ITenatListPayload,
  IUser,
  PrivilegesResponse,
  IApiResponsePunchDashboard,
  IApiTenantResponse,
  IEmployeeScheduleDetailsPayload,
  IApiPayrollResponse,
  IPayrollPayload,
  IReSchedulPayload,
  IPayrollAnEmpPayloadSec,
  IApiPayrollAllResponse,
  IPayrollListApiResponse,
  ISavePayrollAnEmpPayload,
  ISavePayrollAnEmpEachRecordPayload,
  IPayrollAnEmpPayload,
  IDayAnEmpEachRecordPayload,
  IGetRent,
  IDayEmpReportPayload,
  IPayrollPayloadWeeklyReport,
  IApiPayrollGenerateResponse,
  IApiResponseDhashboard,
  IApiResponseDhashbordClient,
  IIdOnlyPayload,
  IEmailRecordPayload,
  IApiSettingResponse,
  IIdOnlyNumberPayload,
  IPayrollAnEmpPayloadSec2,
  IPayrollEmployee,
  IEmployeeScheduleDetailsPayload2,
  IEmployeeScheduleDetailsPayload24,
  IPayrollPayloadEmp,
  IPayrollPayloadEmpNull,
  ISearchEnumPayload,
  ISearchGradePayload,
  ICountApiResponse,
  IClassAsiigenFilter,
  IApiReceiptResponseIdInt,
  IStudentClassPayload,
  IApiResponseUpdate,
  IStudentSubjectByClass,
} from "./Interface/api.interface";
/**
 * Holds all the API callbacks
 * @returns RTK Implementation for backend
 */

export const attoDeskApi = createApi({
  reducerPath: "attoDeskApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers, { endpoint }) => {
      // Most endpoints in this file pre-stringify their body with
      // `body: JSON.stringify(payload)`. RTK Query's fetchBaseQuery
      // does NOT auto-set Content-Type for string bodies (only for
      // plain-object bodies it serialises itself), so removing the
      // JSON header entirely regresses ~130 mutations at once.
      //
      // Solution: keep the JSON default for everyone, but skip it
      // for the small set of endpoints that ship multipart FormData.
      // When we omit the header, the browser sets it to
      // `multipart/form-data; boundary=…` itself. Forcing JSON on a
      // FormData request breaks Laravel's parser and produces the
      // "grade_id / title / worth / student_ids required" phantom
      // rejection seen on the Staff Portal assessment modal.
      //
      // Add any future FormData/multipart mutation names to this
      // set — do NOT set Content-Type in the endpoint's query
      // options (fetchBaseQuery merges prepareHeaders AFTER endpoint
      // headers, so it can't be overridden from there).
      const FORM_DATA_ENDPOINTS: ReadonlySet<string> = new Set([
        "createMyAssessment",
        "uploadHelpImage",
        "uploadHelpVideo",
        // Course attachments POST /course-material/upload as
        // multipart FormData. Without this entry the JSON default
        // above overrides the browser's multipart Content-Type, the
        // request arrives without a boundary, and Laravel's parser
        // sees an empty body — surfacing as
        // "The course id field is required. The file field is required."
        // even though the frontend appended both correctly.
        "uploadCourseMaterial",
      ]);
      if (!FORM_DATA_ENDPOINTS.has(endpoint)) {
        headers.set("Content-Type", "application/json");
      }
      headers.set(
        "Authorization",
        `Bearer ${localStorage.getItem("token") || ""}`
      );
      return headers;
    },
  }),
  tagTypes: [
    "workflowObjective",
    "focusArea",
    "User",
    "Setting",
    "department",
    "User",
    "dashboard",
    "UserRole",
    "Privilege",
    "restaurant",
    "themeColor",
    "team",
    "PunchDetails",
    "EmployeeSchedule",
    "Position",
    "Payroll",
    "Reschedule",
    "email",
    "sms",
    "request",
    "student",
    "grades",
    "location",
    "subject",
    "class",
    "classSchedule",
    "classCancellation",
    "pendingMakeup",
    "attendanceRecord",
    "assessment",
    "course",
    "courseMaterial",
    "assignment",
    "assignmentSubmission",
    "paymentMethod",
    "invoice",
    "invoiceItem",
    "payment",
    "notification",
    "message",
    "messageTemplate",
    "activityLog",
    "permission",
    "rolePermission",
    "staffAvailability",
    "studentNote",
    "helpArticle",
    "programFolder",
    "program",
    "programTarget",
    "promptTemplate",
    "masteryWorkflow",
    "masteryApproval",
    "treatmentArea",
    "measurementDefinition",
    "sessionData",
    "client",
    "parent",
    "ClassScheduleDetails",
    "ClassSchedules",
    "ClassOccurrences",
    "StaffSchedules",
    "StudentSchedules",
    "ParentPortalStatus",
  ],
  endpoints: (builder) => ({
    // Stub for the Lead System's CreateLeadDialog client picker. Step-Up has no
    // customers search endpoint yet, so this returns an empty list (client
    // auto-link is disabled). 🟨 Replace queryFn with a real `/customer/search`
    // query to enable client linking.
    getCustomer: builder.query<IApiResponse, { searchText: string; id: string }>({
      queryFn: () => ({ data: { data: [] } as unknown as IApiResponse }),
    }),
    getUser: builder.query<IApiResponse, void>({
      query: (request) => {
        return {
          url: "/user/userInfo",
          method: "POST",
          body: request,
        };
      },
      // The sidebar menu reads this on every page. The old 1-second cache
      // forced a refetch on every navigation — on `php artisan serve` this
      // was blocking the serialized request queue on every link click.
      // Permissions rarely change mid-session; mutations touching the `User`
      // tag still invalidate immediately.
      keepUnusedDataFor: 60,
      providesTags: ["User"],
    }),
    login: builder.mutation<IApiResponse, IUser>({
      query: (request) => {
        return {
          url: "/login",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
    }),
    forgotFindAccount: builder.mutation<IApiResponse, { login: string }>({
      query: (request) => ({
        url: "/password/forgot/find-account",
        method: "POST",
        body: JSON.stringify(request),
      }),
    }),
    forgotSend: builder.mutation<IApiResponse, { flow_token: string; channel: string }>({
      query: (request) => ({
        url: "/password/forgot/send",
        method: "POST",
        body: JSON.stringify(request),
      }),
    }),
    forgotVerifyCode: builder.mutation<IApiResponse, { flow_token: string; code: string }>({
      query: (request) => ({
        url: "/password/forgot/verify-code",
        method: "POST",
        body: JSON.stringify(request),
      }),
    }),
    forgotResetPassword: builder.mutation<
      IApiResponse,
      { flow_token: string; password: string; password_confirmation: string }
    >({
      query: (request) => ({
        url: "/password/forgot/reset",
        method: "POST",
        body: JSON.stringify(request),
      }),
    }),
    getSetting: builder.query<IApiResponse, ITenatListPayload>({
      query: (request) => {
        return {
          url: "/setting/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Setting"],
      keepUnusedDataFor: 0,
    }),
    createSetting: builder.mutation<IApiSettingResponse, Object>({
      query: (request) => {
        return {
          url: "/setting",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["Setting"],
    }),
    createDepartment: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/department",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["department"],
    }),
    deleteDepartment: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/department/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["department"],
    }),
    getDepartment: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/department/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["department"],
      keepUnusedDataFor: 0,
    }),
    GetAllUserPrivileges: builder.query<PrivilegesResponse, void>({
      query: (request) => {
        return {
          url: "/privilege/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["User"],
      keepUnusedDataFor: 0,
    }),
    createUser: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/user",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["User"],
    }),
    getUserAdminOny: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/user/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["User"],
      keepUnusedDataFor: 0,
    }),
    deleteUser: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/user/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["User"],
    }),
    changeUserPassword: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/user/change-password",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["User"],
    }),
    updateUserPrivileges: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/user/update-privileges",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["User"],
    }),
    getMissingPunchOut: builder.query<IApiResponse, void>({
      query: (request) => {
        return {
          url: "/missing/punch-out",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["dashboard"],
      keepUnusedDataFor: 0,
    }),

    createUserRole: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/user/role",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["UserRole", "User"],
    }),
    deleteUserRole: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/user/role/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["UserRole", "User"],
    }),
    getUserRole: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/user/role/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["UserRole", "User"],
      keepUnusedDataFor: 0,
    }),
    createUserPrivilege: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/user/privilege",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["Privilege", "UserRole", "User"],
    }),
    deleteUserPrivilege: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/user/privilege/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Privilege", "UserRole", "User"],
    }),
    getUserPrivilege: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/user/privilege/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Privilege", "UserRole", "User"],
      keepUnusedDataFor: 0,
    }),
    getRestaurant: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/restaurant/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["restaurant"],
      keepUnusedDataFor: 0,
    }),
    createRestaurant: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/restaurant",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["restaurant"],
    }),
    deleteRestaurant: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/restaurant/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["restaurant"],
    }),
    deleteRestaurantSchedule: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/restaurant/schedule/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["restaurant"],
    }),
    getDashboardSecSectionOrderList: builder.query<IApiResponse, void>({
      query: (request) => {
        return {
          url: "/dashboard/sec/section/order/list/getAll",
          method: "POST",
          body: request,
        };
      },
      keepUnusedDataFor: 1,
      providesTags: ["dashboard"],
    }),
    getUserInfoOny: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/user/getAll/info",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["User"],
      keepUnusedDataFor: 0,
    }),
    createLightThemeColor: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/light/theme/color",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["themeColor"],
    }),
    getAllLightThemeColor: builder.query<
      IApiResponse,
      ILoopingTableListPayload
    >({
      query: (request) => {
        return {
          url: "/light/theme/color/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["themeColor"],
      keepUnusedDataFor: 0,
    }),
    createDarkThemeColor: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/dark/theme/color",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["themeColor"],
    }),
    getAllDarkThemeColor: builder.query<IApiResponse, ILoopingTableListPayload>(
      {
        query: (request) => {
          return {
            url: "/dark/theme/color/getAll",
            method: "POST",
            body: request,
          };
        },
        providesTags: ["themeColor"],
        keepUnusedDataFor: 0,
      }
    ),
    createLightThemeColorOder: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/light/theme/color/order",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["themeColor"],
    }),
    createDarkThemeColorOrder: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/dark/theme/color/order",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["themeColor"],
    }),
    createTeam: builder.mutation<IApiTenantResponse, Object>({
      query: (request) => {
        return {
          url: "/team",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["team", "request"],
    }),
    getTeam: builder.query<IApiResponse, ITeamListPayload>({
      query: (request) => {
        return {
          url: "/team/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team"],
      // Dropdown lookup — mutations invalidate the `team` tag so a longer
      // cache is safe. 10 minutes keeps page-to-page navigation cheap on the
      // single-threaded dev server; tag invalidation still refreshes it
      // immediately after any team mutation.
      keepUnusedDataFor: 600,
    }),
    // Lightweight Staff/Student options for the Class Schedule filters. Gated by
    // ClassSchedule_View, so a schedule viewer always gets its filter lists.
    getClassScheduleFilterOptions: builder.query<IApiResponse, { startDate?: string; endDate?: string } | void>({
      query: (args) => {
        const p = new URLSearchParams();
        if ((args as any)?.startDate) p.set("startDate", (args as any).startDate);
        if ((args as any)?.endDate) p.set("endDate", (args as any).endDate);
        const qs = p.toString();
        return { url: `/class-schedule/filter-options${qs ? `?${qs}` : ""}`, method: "GET" };
      },
      // Refresh when staff, students or schedules change so options never go stale
      // (the Student list is scoped to students assigned within the date window).
      providesTags: ["team", "student", "classSchedule"],
      // 5 minutes — tag-invalidated on any staff/student/schedule mutation, so
      // the only cost of a longer cache is a stale list during long idle time.
      keepUnusedDataFor: 300,
    }),
    // Staff Portal — self-scoped filter options for the Class Sessions list.
    // Backed by StaffTeachingScopeService: staff = self, students = only
    // actively-enrolled in own occurrences, locations/grades/subjects = only
    // from own occurrences. Admins hitting this endpoint receive the same
    // self-scoped view (they should use the admin endpoint for academy-wide).
    getStaffPortalClassSessionsFilterOptions: builder.query<IApiResponse, void>({
      query: () => ({ url: "/staff-portal/class-sessions/filter-options", method: "GET" }),
      providesTags: ["team", "student", "classSchedule"],
      keepUnusedDataFor: 60,
    }),
    // "Today" weekly timetable — one payload with 7-day tabs, staff rows,
    // enriched occurrences, and selected-date summary cards. Filters go in
    // query string so RTK caches per filter combo.
    getTimetableWeek: builder.query<IApiResponse, {
      reference_date?: string;
      selected_date?: string;
      location_id?: number | string;
      mode?: "online" | "in_person";
      staff_id?: number | string;
    }>({
      query: (args = {}) => {
        const p = new URLSearchParams();
        if (args.reference_date) p.set("reference_date", args.reference_date);
        if (args.selected_date)  p.set("selected_date",  args.selected_date);
        if (args.location_id)    p.set("location_id",    String(args.location_id));
        if (args.mode)           p.set("mode",           args.mode);
        if (args.staff_id)       p.set("staff_id",       String(args.staff_id));
        const qs = p.toString();
        return { url: `/timetable/week${qs ? `?${qs}` : ""}`, method: "GET" };
      },
      // Invalidated by anything that alters occurrences, attendance, or invoices —
      // the star indicator depends on the invoice tag staying fresh.
      providesTags: ["classSchedule", "attendanceRecord", "invoice", "student", "team", "ClassOccurrences"],
      keepUnusedDataFor: 30,
    }),
    getStaffDashboardStats: builder.query<IApiResponse, any>({
      query: (r) => ({ url: "/team/dashboard-stats", method: "POST", body: r }),
      providesTags: ["team", "classSchedule"],
      keepUnusedDataFor: 30,
    }),
    // Complete teaching profile for one staff (counts + subjects tree + weekly
    // schedule from templates + today's occurrences).
    getStaffTeachingProfile: builder.query<IApiResponse, { staffId: string | number }>({
      query: ({ staffId }) => ({ url: `/team/${staffId}/teaching-profile`, method: "GET" }),
      providesTags: ["team", "classSchedule"],
      keepUnusedDataFor: 0,
    }),
    // Dated class occurrences (calendar view) for a staff member + date range.
    getStaffCalendar: builder.query<IApiResponse, { staffId: string | number; start: string; end: string }>({
      query: ({ staffId, start, end }) => ({ url: `/team/${staffId}/calendar?start=${start}&end=${end}`, method: "GET" }),
      providesTags: ["team", "classSchedule"],
      keepUnusedDataFor: 0,
    }),
    // Self-only calendar for the Staff Portal "My Day" dashboard.
    // Uses Auth::user()->staffId server-side — no staffId param on
    // the client, no IDOR surface. Gated by Dashboard_View so the
    // seeded Staff role can call it (which does not hold Staff_View,
    // the gate on the admin /team/{staff}/calendar endpoint).
    getMyStaffCalendar: builder.query<IApiResponse, { start: string; end: string }>({
      query: ({ start, end }) => ({ url: `/staff-portal/my-calendar?start=${start}&end=${end}`, method: "GET" }),
      providesTags: ["team", "classSchedule", "attendanceRecord"],
      keepUnusedDataFor: 0,
    }),
    // Staff Portal Phase 3a — occurrence-scoped student surfaces.
    // Both use Auth::user()->staffId server-side; no staffId param
    // on the client. Every DTO field is staff-safe (no billing,
    // portal credentials, or parent admin data). Backend enforces
    // canAccessStudent for the detail endpoint — direct URL hit
    // to a foreign student id returns 403.
    getMyStudents: builder.query<IApiResponse, void>({
      query: () => ({ url: "/staff-portal/my-students", method: "GET" }),
      providesTags: ["team", "student", "classSchedule"],
      keepUnusedDataFor: 30,
    }),
    // Staff Portal Create Class hydration source (2026-08-25).
    // Auth-derived; the backend rejects a foreign staff_id — never
    // trust a URL param. Response shape:
    //   data: { staff: { id, name }, assignments: [ { subject_id,
    //     subject_name, grades: [{ grade_id, grade_name,
    //     subject_grade_id }] } ], is_admin }
    getMyTeachingAssignments: builder.query<IApiResponse, void>({
      query: () => ({ url: "/staff-portal/my-teaching-assignments", method: "GET" }),
      providesTags: ["team"],
      keepUnusedDataFor: 60,
    }),
    // Staff Portal — My Profile (view-only). Auth-derived on the
    // backend; no staff_id param, no IDOR surface. Response:
    //   data: {
    //     staff: { id, reference_id, first_name, last_name, email,
    //              phone, alt_phone, role_label, status, avatar_url,
    //              username, portal_status, last_login_at },
    //     locations: [{ id, name }],
    //     teaching_assignments: [{ subject_id, subject_name,
    //                              grades: [{ grade_id, grade_name, subject_grade_id }] }],
    //     has_aba_caseload: boolean,
    //   }
    getMyProfile: builder.query<IApiResponse, void>({
      query: () => ({ url: "/staff-portal/profile", method: "GET" }),
      providesTags: ["team", "User"],
      keepUnusedDataFor: 60,
    }),
    // Catalogs consumed only when the acting staff enters edit mode
    // on the My Profile page. Kept separate from the profile GET so
    // the initial view-mode render stays lean (subjects×grades can
    // be a couple hundred rows).
    // Response data shape:
    //   {
    //     subjects:  [{ subject_id, subject_name,
    //                   grades: [{ grade_id, grade_name, subject_grade_id }] }],
    //     locations: [{ id, name }]
    //   }
    getMyProfileEditOptions: builder.query<IApiResponse, void>({
      query: () => ({ url: "/staff-portal/profile/edit-options", method: "GET" }),
      keepUnusedDataFor: 300,
    }),
    // Self-service PATCH for the My Profile page. Body is a strict
    // whitelist enforced server-side (email, phone, alt_phone,
    // location_ids[], subject_grade_ids[]) — extra fields are
    // silently ignored. Response mirrors GET /staff-portal/profile
    // so the "team" tag invalidation is enough to refresh the view.
    updateMyProfile: builder.mutation<
      IApiResponse,
      Partial<{
        email: string | null;
        phone: string | null;
        alt_phone: string | null;
        location_ids: number[];
        subject_grade_ids: number[];
      }>
    >({
      query: (body) => ({
        url: "/staff-portal/profile",
        method: "PATCH",
        body: JSON.stringify(body),
      }),
      invalidatesTags: ["team", "User"],
    }),
    // Staff Portal self-service class creation (2026-08-25).
    // Distinct endpoint from POST /assign/classes so the "always
    // create for the acting staff" contract lives server-side in a
    // dedicated controller path — not gated by a forgeable
    // frontend boolean. Admin/management callers keep using the
    // existing /assign/classes flow.
    storeMyClass: builder.mutation<IApiResponse, Object>({
      query: (request) => ({
        url: "/staff-portal/classes",
        method: "POST",
        body: JSON.stringify(request),
      }),
      invalidatesTags: ["team", "class", "classSchedule"],
    }),
    getMyStudentDetail: builder.query<IApiResponse, { studentId: number | string }>({
      query: ({ studentId }) => ({ url: `/staff-portal/students/${studentId}`, method: "GET" }),
      providesTags: (_r, _e, arg) => [{ type: "student" as const, id: Number(arg.studentId) }, "team", "classSchedule"],
      keepUnusedDataFor: 30,
    }),
    // Staff-scoped student calendar events for Month/Week/Agenda.
    // Bounded window (max ~62 days server-side).
    getMyStudentCalendar: builder.query<IApiResponse, { studentId: number | string; from: string; to: string }>({
      query: ({ studentId, from, to }) => ({
        url: `/staff-portal/students/${studentId}/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        method: "GET",
      }),
      providesTags: (_r, _e, arg) => [{ type: "student" as const, id: Number(arg.studentId) }, "team", "classSchedule"],
      keepUnusedDataFor: 30,
    }),
    // Staff Portal Phase 3b — self-scoped assessments + assignments.
    // Row-level scope enforced by StaffTeachingScopeService in the
    // backend (canAccessAssessment: assessments.staff_id === user.staffId;
    // canAccessAssignment: assignments.created_by === user.id).
    getMyAssessments: builder.query<IApiResponse, { page?: number; per_page?: number; status?: string; search?: string; student_id?: number | string; from?: string; to?: string } | void>({
      query: (params) => {
        const p = (params ?? {}) as Record<string, any>;
        const q = new URLSearchParams();
        Object.entries(p).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") q.set(k, String(v)); });
        const qs = q.toString();
        return { url: `/staff-portal/assessments${qs ? `?${qs}` : ""}`, method: "GET" };
      },
      providesTags: ["team", "student"],
      keepUnusedDataFor: 30,
    }),
    getMyAssessmentDetail: builder.query<IApiResponse, { assessmentId: number | string }>({
      query: ({ assessmentId }) => ({ url: `/staff-portal/assessments/${assessmentId}`, method: "GET" }),
      providesTags: ["team"],
      keepUnusedDataFor: 30,
    }),
    // Staff-scoped grade + feedback write. Body accepts student_mark
    // (numeric, 0..max_value) and comment_1/2/3 + analysis. Server
    // recomputes percentage + pass_fail + stamps graded_by/graded_at.
    // Invalidates "team" so the assessments list refetches counts.
    // Staff Portal Report Card — composed aggregation for one student.
    // Backend enforces canAccessStudent; params: from/to (Y-m-d).
    getMyStudentReportCard: builder.query<IApiResponse, { studentId: number | string; from?: string; to?: string }>({
      query: ({ studentId, from, to }) => {
        const q = new URLSearchParams();
        if (from) q.set("from", from);
        if (to) q.set("to", to);
        const qs = q.toString();
        return { url: `/staff-portal/reports/students/${studentId}/report-card${qs ? `?${qs}` : ""}`, method: "GET" };
      },
      providesTags: ["team", "student"],
      keepUnusedDataFor: 30,
    }),
    updateMyAssessment: builder.mutation<IApiResponse, { assessmentId: number | string; student_mark?: number | null; comment_1?: string | null; comment_2?: string | null; comment_3?: string | null; analysis?: string | null }>({
      query: ({ assessmentId, ...body }) => ({
        url: `/staff-portal/assessments/${assessmentId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["team"],
    }),
    // Staff-scoped assessment create — options + POST. Options endpoint
    // returns ONLY the (grade,subject,students) tree the acting staff
    // actually teaches; server re-validates every id on POST so a
    // manipulated client cannot broaden scope. Invalidates staff
    // assessment list on success.
    getMyAssessmentOptions: builder.query<IApiResponse, void>({
      query: () => ({ url: "/staff-portal/assessments/options", method: "GET" }),
      providesTags: ["team", "student"],
      keepUnusedDataFor: 60,
    }),
    // ── Phase 3c — Staff Portal messages ─────────────────────────
    // Self-scoped inbox (source='portal' only). Server enforces:
    // participant in the row; new sends require CURRENT teaching
    // scope; recipient must be a parent linked to that student.
    getMyMessages: builder.query<IApiResponse, { page?: number; per_page?: number; filter?: "inbox" | "sent" | "unread" | "all"; search?: string } | void>({
      query: (params) => {
        const p = (params ?? {}) as Record<string, any>;
        const q = new URLSearchParams();
        Object.entries(p).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") q.set(k, String(v)); });
        const qs = q.toString();
        return { url: `/staff-portal/messages${qs ? `?${qs}` : ""}`, method: "GET" };
      },
      providesTags: ["message"],
      keepUnusedDataFor: 30,
    }),
    getMyMessageThread: builder.query<IApiResponse, { rootId: number | string }>({
      query: ({ rootId }) => ({ url: `/staff-portal/messages/thread/${rootId}`, method: "GET" }),
      providesTags: (_r, _e, arg) => [{ type: "message" as const, id: Number(arg.rootId) }],
      keepUnusedDataFor: 30,
    }),
    getMyMessageRecipients: builder.query<IApiResponse, { student_id: number | string }>({
      query: ({ student_id }) => ({ url: `/staff-portal/messages/eligible-recipients?student_id=${student_id}`, method: "GET" }),
      keepUnusedDataFor: 60,
    }),
    sendMyMessage: builder.mutation<IApiResponse, {
      student_id: number;
      recipient_user_id: number;
      subject: string;
      body: string;
      class_occurrence_id?: number;
      parent_message_id?: number;
    }>({
      query: (body) => ({ url: "/staff-portal/messages", method: "POST", body }),
      invalidatesTags: ["message"],
    }),
    markMyMessageRead: builder.mutation<IApiResponse, { id: number }>({
      query: (body) => ({ url: "/staff-portal/messages/mark-read", method: "POST", body }),
      invalidatesTags: ["message"],
    }),
    // Staff → Admin/Office recipients + send. Backend re-verifies the
    // recipient is a current System_Admin holder on every send —
    // client dropdown is UX only.
    getMyAdminMessageRecipients: builder.query<IApiResponse, void>({
      query: () => ({ url: "/staff-portal/messages/admin-recipients", method: "GET" }),
      keepUnusedDataFor: 60,
    }),
    sendMyAdminMessage: builder.mutation<IApiResponse, {
      recipient_user_id: number;
      subject: string;
      body: string;
    }>({
      query: (body) => ({ url: "/staff-portal/messages/admin", method: "POST", body }),
      invalidatesTags: ["message"],
    }),
    // ── Staff Portal message templates ────────────────────────
    // Effective list (system defaults + user overrides + user
    // customs) for the template management page.
    getMyMessageTemplates: builder.query<IApiResponse, void>({
      query: () => ({ url: "/staff-portal/message-templates", method: "GET" }),
      providesTags: ["messageTemplate"],
      keepUnusedDataFor: 30,
    }),
    // Active-only feed for the New Message composer chips. Separate
    // hook so the composer stays snappy even if the management page
    // has an in-flight refresh.
    getMyActiveMessageTemplates: builder.query<IApiResponse, void>({
      query: () => ({ url: "/staff-portal/message-templates/active", method: "GET" }),
      providesTags: ["messageTemplate"],
      keepUnusedDataFor: 30,
    }),
    createMyMessageTemplate: builder.mutation<IApiResponse, {
      name: string; subject_template: string; body_template: string;
      active?: boolean; sort_order?: number;
    }>({
      query: (body) => ({ url: "/staff-portal/message-templates", method: "POST", body: JSON.stringify(body) }),
      invalidatesTags: ["messageTemplate"],
    }),
    updateMyMessageTemplate: builder.mutation<IApiResponse, {
      id: number;
      name?: string; subject_template?: string; body_template?: string;
      active?: boolean; sort_order?: number;
    }>({
      query: ({ id, ...body }) => ({ url: `/staff-portal/message-templates/${id}`, method: "PUT", body: JSON.stringify(body) }),
      invalidatesTags: ["messageTemplate"],
    }),
    activateMyMessageTemplate: builder.mutation<IApiResponse, { id: number }>({
      query: ({ id }) => ({ url: `/staff-portal/message-templates/${id}/activate`, method: "POST" }),
      invalidatesTags: ["messageTemplate"],
    }),
    deactivateMyMessageTemplate: builder.mutation<IApiResponse, { id: number }>({
      query: ({ id }) => ({ url: `/staff-portal/message-templates/${id}/deactivate`, method: "POST" }),
      invalidatesTags: ["messageTemplate"],
    }),
    resetMyMessageTemplate: builder.mutation<IApiResponse, { id: number }>({
      query: ({ id }) => ({ url: `/staff-portal/message-templates/${id}/reset`, method: "POST" }),
      invalidatesTags: ["messageTemplate"],
    }),
    // Delete is only wired for caller-owned custom templates. The
    // menu hides the action for system defaults; the backend also
    // rejects 422 on system defaults and 403 on other staff's rows.
    deleteMyMessageTemplate: builder.mutation<IApiResponse, { id: number }>({
      query: ({ id }) => ({ url: `/staff-portal/message-templates/${id}`, method: "DELETE" }),
      invalidatesTags: ["messageTemplate"],
    }),
    // ── Attendance Messaging Phase 2 — tenant/global templates ────
    // Composer read (context-scoped, active-only). Kept lightweight
    // so the composer opens instantly; admin edits invalidate the
    // shared "messageTemplate" tag to refresh both surfaces.
    getAttendanceMessageTemplates: builder.query<IApiResponse, { context?: string } | void>({
      query: (params) => {
        const ctx = (params && (params as any).context) || "attendance";
        return { url: `/class-sessions/message-templates?context=${encodeURIComponent(ctx)}`, method: "GET" };
      },
      providesTags: ["messageTemplate"],
      keepUnusedDataFor: 30,
    }),
    // Admin Settings — full tenant template list (System_Admin).
    getAdminMessageTemplates: builder.query<IApiResponse, { context?: string } | void>({
      query: (params) => {
        const ctx = (params && (params as any).context) || "attendance";
        return { url: `/admin/message-templates?context=${encodeURIComponent(ctx)}`, method: "GET" };
      },
      providesTags: ["messageTemplate"],
      keepUnusedDataFor: 30,
    }),
    updateAdminMessageTemplate: builder.mutation<IApiResponse, {
      id: number;
      name?: string; subject_template?: string; body_template?: string;
      sort_order?: number;
    }>({
      query: ({ id, ...body }) => ({ url: `/admin/message-templates/${id}`, method: "PUT", body: JSON.stringify(body) }),
      invalidatesTags: ["messageTemplate"],
    }),
    activateAdminMessageTemplate: builder.mutation<IApiResponse, { id: number }>({
      query: ({ id }) => ({ url: `/admin/message-templates/${id}/activate`, method: "POST" }),
      invalidatesTags: ["messageTemplate"],
    }),
    deactivateAdminMessageTemplate: builder.mutation<IApiResponse, { id: number }>({
      query: ({ id }) => ({ url: `/admin/message-templates/${id}/deactivate`, method: "POST" }),
      invalidatesTags: ["messageTemplate"],
    }),
    resetAdminMessageTemplate: builder.mutation<IApiResponse, { id: number }>({
      query: ({ id }) => ({ url: `/admin/message-templates/${id}/reset`, method: "POST" }),
      invalidatesTags: ["messageTemplate"],
    }),
    // ── Phase 3c — Staff Portal parent requests ──────────────────
    getMyParentRequests: builder.query<IApiResponse, { page?: number; per_page?: number; status?: string; request_type?: string; search?: string } | void>({
      query: (params) => {
        const p = (params ?? {}) as Record<string, any>;
        const q = new URLSearchParams();
        Object.entries(p).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") q.set(k, String(v)); });
        const qs = q.toString();
        return { url: `/staff-portal/parent-requests${qs ? `?${qs}` : ""}`, method: "GET" };
      },
      providesTags: ["classSchedule", "student"],
      keepUnusedDataFor: 15,
    }),
    getMyParentRequestDetail: builder.query<IApiResponse, { id: number | string }>({
      query: ({ id }) => ({ url: `/staff-portal/parent-requests/${id}`, method: "GET" }),
      providesTags: (_r, _e, arg) => [{ type: "classSchedule" as const, id: Number(arg.id) }],
      keepUnusedDataFor: 15,
    }),
    handleMyParentRequest: builder.mutation<IApiResponse, { id: number; status: "approved" | "rejected"; admin_notes?: string }>({
      query: ({ id, ...body }) => ({ url: `/staff-portal/parent-requests/${id}/handle`, method: "POST", body }),
      invalidatesTags: ["classSchedule", "student"],
    }),
    createMyAssessment: builder.mutation<IApiResponse, {
      grade_id: number;
      subject_id: number;
      student_ids: number[];
      title: string;
      assessment_type: "test" | "quiz" | "homework" | "observation" | "project" | "other";
      date: string;
      due_date?: string;
      worth: number;
      pass_threshold?: number;
      focus?: string;
      instructions?: string;
      class_id?: number;
      attachments?: File[];
      youtube_url?: string;
    }>({
      query: (body) => {
        // If Learning Materials (files or YouTube URL) are present,
        // ship as multipart. Otherwise keep the existing JSON body so
        // legacy consumers of the endpoint are unaffected.
        const hasFiles = Array.isArray(body.attachments) && body.attachments.length > 0;
        const hasYoutube = typeof body.youtube_url === "string" && body.youtube_url.trim() !== "";
        if (hasFiles || hasYoutube) {
          const fd = new FormData();
          fd.append("grade_id", String(body.grade_id));
          fd.append("subject_id", String(body.subject_id));
          // Laravel wants an array here. `JSON.stringify(...)` sends a
          // string which fails `required|array`; appending `student_ids[]`
          // once per id is the standard PHP-friendly encoding.
          body.student_ids.forEach((sid) => fd.append("student_ids[]", String(sid)));
          fd.append("title", body.title);
          fd.append("assessment_type", body.assessment_type);
          fd.append("date", body.date);
          if (body.due_date) fd.append("due_date", body.due_date);
          fd.append("worth", String(body.worth));
          if (typeof body.pass_threshold === "number") fd.append("pass_threshold", String(body.pass_threshold));
          if (body.focus) fd.append("focus", body.focus);
          if (body.instructions) fd.append("instructions", body.instructions);
          if (typeof body.class_id === "number") fd.append("class_id", String(body.class_id));
          if (hasYoutube) fd.append("youtube_url", (body.youtube_url as string).trim());
          if (hasFiles) (body.attachments as File[]).forEach((f) => fd.append("attachments[]", f));
          return { url: "/staff-portal/assessments", method: "POST", body: fd };
        }
        return { url: "/staff-portal/assessments", method: "POST", body };
      },
      invalidatesTags: ["team", "student"],
    }),
    getMyAssignments: builder.query<IApiResponse, { page?: number; per_page?: number; status?: string; search?: string; due_from?: string; due_to?: string } | void>({
      query: (params) => {
        const p = (params ?? {}) as Record<string, any>;
        const q = new URLSearchParams();
        Object.entries(p).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") q.set(k, String(v)); });
        const qs = q.toString();
        return { url: `/staff-portal/assignments${qs ? `?${qs}` : ""}`, method: "GET" };
      },
      providesTags: ["team"],
      keepUnusedDataFor: 30,
    }),
    getMyAssignmentDetail: builder.query<IApiResponse, { assignmentId: number | string }>({
      query: ({ assignmentId }) => ({ url: `/staff-portal/assignments/${assignmentId}`, method: "GET" }),
      providesTags: ["team"],
      keepUnusedDataFor: 30,
    }),
    // Assigned class SCHEDULE PATTERNS for a staff member (one row per
    // class_id + schedule_id, never per generated occurrence). Powers the
    // "Class Schedules" tab on the Staff Profile page.
    getStaffClassSchedules: builder.query<IApiResponse, {
      staffId: string | number;
      days?: string[];
      from_time?: string;
      to_time?: string;
      grade_ids?: number[];
      subject_ids?: number[];
      class_types?: string[];
      study_modes?: string[];
      location_ids?: number[];
      enrollment?: string;
      search?: string;
      sort?: string;
      page?: number;
      per_page?: number;
    }>({
      query: ({ staffId, ...params }) => {
        const q = new URLSearchParams();
        const push = (k: string, v: any) => {
          if (Array.isArray(v)) v.forEach((x) => q.append(`${k}[]`, String(x)));
          else if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
        };
        push("days", params.days);
        push("from_time", params.from_time);
        push("to_time", params.to_time);
        push("grade_ids", params.grade_ids);
        push("subject_ids", params.subject_ids);
        push("class_types", params.class_types);
        push("study_modes", params.study_modes);
        push("location_ids", params.location_ids);
        push("enrollment", params.enrollment);
        push("search", params.search);
        push("sort", params.sort);
        push("page", params.page);
        push("per_page", params.per_page);
        const qs = q.toString();
        return { url: `/team/${staffId}/class-schedules${qs ? `?${qs}` : ""}`, method: "GET" };
      },
      providesTags: ["team", "classSchedule", "class"],
      keepUnusedDataFor: 0,
    }),
    // Global class SCHEDULE PATTERNS across all staff — one row per
    // (class_id, schedule_id). Powers the standalone Class Schedules page.
    getAllClassSchedules: builder.query<IApiResponse, {
      search?: string;
      days?: string[];
      from_time?: string;
      to_time?: string;
      staff_ids?: number[];
      grade_ids?: number[];
      subject_ids?: number[];
      class_types?: string[];
      program_types?: string[];
      study_modes?: string[];
      location_ids?: number[];
      enrollment?: string;
      status?: string[];
      sort?: string;
      page?: number;
      per_page?: number;
    }>({
      query: (params) => {
        const q = new URLSearchParams();
        const push = (k: string, v: any) => {
          if (Array.isArray(v)) v.forEach((x) => q.append(`${k}[]`, String(x)));
          else if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
        };
        Object.entries(params).forEach(([k, v]) => push(k, v));
        const qs = q.toString();
        return { url: `/class-schedules${qs ? `?${qs}` : ""}`, method: "GET" };
      },
      providesTags: ["classSchedule", "class", "team"],
      keepUnusedDataFor: 0,
    }),
    getClassScheduleEligibleStudents: builder.query<IApiResponse, { scheduleId: number | string }>({
      query: ({ scheduleId }) => ({ url: `/class-schedules/${scheduleId}/eligible-students`, method: "GET" }),
      providesTags: ["classSchedule"],
      keepUnusedDataFor: 0,
    }),
    getClassScheduleEligibleStaff: builder.query<IApiResponse, { scheduleId: number | string }>({
      query: ({ scheduleId }) => ({ url: `/class-schedules/${scheduleId}/eligible-staff`, method: "GET" }),
      providesTags: ["classSchedule"],
      keepUnusedDataFor: 0,
    }),
    /** Smart Match replacement staff for a WHOLE pattern + effective date.
     *  Hard-filters by qualification/availability/conflicts across every
     *  future occurrence; mirrors the save-time guard so a ranked candidate
     *  is one the server will actually accept. */
    getClassSchedulePatternReplacementStaff: builder.mutation<IApiResponse, { scheduleId: number | string; effective_date: string }>({
      query: ({ scheduleId, ...body }) => ({ url: `/class-schedules/${scheduleId}/replacement-staff`, method: "POST", body }),
    }),
    addClassScheduleStudent: builder.mutation<IApiResponse, { scheduleId: number | string; student_id: number; effective_date?: string }>({
      query: ({ scheduleId, ...body }) => ({ url: `/class-schedules/${scheduleId}/add-student`, method: "POST", body }),
      invalidatesTags: ["classSchedule", "class", "team", "attendanceRecord"],
    }),
    releaseClassScheduleStudent: builder.mutation<IApiResponse, { scheduleId: number | string; student_id: number; effective_date: string; reason: string; notes?: string }>({
      query: ({ scheduleId, ...body }) => ({ url: `/class-schedules/${scheduleId}/release-student`, method: "POST", body }),
      invalidatesTags: ["classSchedule", "class", "team", "attendanceRecord"],
    }),
    switchClassScheduleStaff: builder.mutation<IApiResponse, { scheduleId: number | string; staff_id: number; effective_date: string; reason: string; notes?: string }>({
      query: ({ scheduleId, ...body }) => ({ url: `/class-schedules/${scheduleId}/switch-staff`, method: "POST", body }),
      invalidatesTags: ["classSchedule", "class", "team", "attendanceRecord"],
    }),
    // Class TEMPLATE (assign_classes) details — powers /class-schedules/{classId}
    // and /class-schedules/{classId}/edit pages.
    getClassScheduleDetails: builder.query<IApiResponse, number | string>({
      query: (classId) => ({ url: `/class-schedules/${classId}/details`, method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "ClassScheduleDetails" as const, id }],
      keepUnusedDataFor: 0,
    }),
    updateClassScheduleTemplate: builder.mutation<IApiResponse, {
      classId: number | string;
      staff_id: number;
      teaching_mode: string;
      location_id?: number | null;
      class_type: string;
      program_type?: string;
      capacity?: number | null;
      all_subject_grade_ids: number[];
      new_subject_grade_ids: number[];
      existing_subject_grade_ids: number[];
      schedules: { day: string; start_time: string; end_time: string; period_start_date?: string | null; period_end_date?: string | null }[];
      effective_date?: string;
    }>({
      query: ({ classId, ...body }) => ({ url: `/class-schedules/${classId}`, method: "PUT", body }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "ClassScheduleDetails" as const, id: arg.classId },
        "ClassSchedules", "ClassOccurrences", "StaffSchedules", "StudentSchedules",
        "classSchedule", "class", "team", "student", "attendanceRecord",
      ],
    }),
    getClassDetail: builder.query<IApiResponse, { classId: string | number; occurrenceId?: string | number }>({
      query: ({ classId, occurrenceId }) => ({
        url: `/classes/${classId}/detail${occurrenceId ? `?occurrence_id=${occurrenceId}` : ""}`,
        method: "GET",
      }),
      // ID-scoped tag so mutating ANOTHER class (e.g. assignClassesCreate) does
      // not invalidate this detail cache. The plain-string tag used to blow
      // away every open detail page the moment any class in the system was
      // mutated; now only mutations that specifically touch this class id
      // (via { type: "classSchedule", id: <classId> }) refresh it. The plain
      // "classSchedule" tag emitted by broad mutations still invalidates list
      // queries that provide the plain tag.
      providesTags: (_r, _e, arg) => [{ type: "classSchedule" as const, id: String(arg.classId) }],
      // Keep the detail cache for 5 minutes so Detail → Create → Back renders
      // instantly from cache instead of flashing a full-page skeleton.
      keepUnusedDataFor: 300,
    }),
    // Grade-filtered students eligible to be added to a specific class, plus that
    // class's read-only prefill context (subject/grade/staff/mode/location/schedule).
    getClassEligibleStudents: builder.query<
      IApiResponse,
      { classId: string | number; search?: string; all?: boolean }
    >({
      query: ({ classId, search, all }) => {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (all) params.set("all", "1");
        const qs = params.toString();
        return {
          url: `/classes/${classId}/eligible-students${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
      providesTags: ["classSchedule", "student"],
      keepUnusedDataFor: 0,
    }),
    deleteTeam: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/team/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["team"],
    }),
    updateTeamStatus: builder.mutation<IApiResponse, { id: number | string; status: "active" | "inactive" }>({
      query: ({ id, status }) => ({
        url: `/team/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["team"],
    }),
    getPunchDetails: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/punch/details/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["PunchDetails"],
      keepUnusedDataFor: 0,
    }),
    getPunchDashboard: builder.query<IApiResponsePunchDashboard, void>({
      query: (request) => {
        return {
          url: "/dashboard/punch/details",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["PunchDetails"],
      keepUnusedDataFor: 0,
    }),
    deleteEmployeeSchedule: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/employee/schedule/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["team"],
    }),
    getEmployeeScheduleDetails: builder.query<
      IApiResponse,
      IEmployeeScheduleDetailsPayload
    >({
      query: (request) => {
        return {
          url: "/employee/schedule/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["EmployeeSchedule", "PunchDetails"],
      keepUnusedDataFor: 0,
    }),
    getEmployeeScheduleDetailsByDate: builder.query<
      IApiResponse,
      IEmployeeScheduleDetailsPayload
    >({
      query: (request) => {
        return {
          url: "/employee/schedule/date/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["EmployeeSchedule", "PunchDetails"],
      keepUnusedDataFor: 0,
    }),
    getEmployeeImg: builder.query<
      IApiResponseForPunch,
      IEmployeePunchImgPayload
    >({
      query: (request) => {
        return {
          url: "/punch/details/get-img",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["EmployeeSchedule", "PunchDetails"],
      keepUnusedDataFor: 0,
    }),
    createPosition: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/position",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["Position"],
    }),
    deletePosition: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/position/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Position"],
    }),
    getPosition: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/position/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Position"],
      keepUnusedDataFor: 0,
    }),
    getEmployeePayRoll: builder.query<IApiPayrollResponse, IPayrollPayload>({
      query: (request) => {
        return {
          url: "/team/payroll/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Payroll"],
      keepUnusedDataFor: 0,
    }),
    createAdminEditPunch: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/punch/details/admin/edit",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["PunchDetails"],
    }),

    getEmployeePayRollAdmin: builder.query<
      IApiPayrollResponse,
      IPayrollPayload
    >({
      query: (request) => {
        return {
          url: "/punch/record/payroll-generate",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Payroll"],
      keepUnusedDataFor: 0,
    }),
    getlastUpdateDatePayroll: builder.query<IApiPayrollResponse, void>({
      query: (request) => {
        return {
          url: "/last-update/payroll-date",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Payroll"],
      keepUnusedDataFor: 0,
    }),
    createReschedule: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/emp/schedule/record",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["Reschedule"],
    }),
    deleteReschedule: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/emp/schedule/record/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Reschedule"],
    }),
    getReschedule: builder.query<IApiResponse, IReSchedulPayload>({
      query: (request) => {
        return {
          url: "/emp/schedule/record/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Reschedule"],
      keepUnusedDataFor: 0,
    }),
    getTeamSerchOnly: builder.query<IApiResponse, ITeamListPayload>({
      query: (request) => {
        return {
          url: "/team/search/only",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team"],
      keepUnusedDataFor: 0,
    }),
    getEmployeeScheduleDetailsByDateOnly: builder.query<
      IApiResponse,
      IEmployeeScheduleDetailsPayload24
    >({
      query: (request) => {
        return {
          url: "/employee/schedule/date/only",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["EmployeeSchedule", "PunchDetails"],
      keepUnusedDataFor: 0,
    }),
    removedReschedule: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/emp/schedule/record/removed",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["Reschedule"],
    }),
    getRescheduleOrderByDate: builder.query<IApiResponse, IReSchedulPayload>({
      query: (request) => {
        return {
          url: "/emp/schedule/record/getAll/orderBy/Date",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Reschedule"],
      keepUnusedDataFor: 0,
    }),
    getEmployeePayRollAdminUse: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/payroll/generate",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["Payroll"],
    }),
    getAnEmployeePayRollAdmin: builder.query<
      IApiPayrollResponse,
      IPayrollAnEmpPayloadSec
    >({
      query: (request) => {
        return {
          url: "/punch/record/employee/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Payroll"],
      keepUnusedDataFor: 0,
    }),
    getAllPayrollList: builder.query<IApiResponse, void>({
      query: () => {
        return {
          url: "/payroll/list/getAll",
          method: "POST",
        };
      },
      providesTags: ["Payroll"],
      keepUnusedDataFor: 0,
    }),
    getAllPayrollRecordeDateRange: builder.query<IApiPayrollAllResponse, void>({
      query: (request) => {
        return {
          url: "/payroll/record/date-range/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Payroll"],
      keepUnusedDataFor: 0,
    }),
    getSaveEmployeePayrollRecord: builder.query<
      IPayrollListApiResponse,
      IEmployeePunchImgPayload
    >({
      query: (request) => {
        return {
          url: "/save/payroll/List/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team"],
      keepUnusedDataFor: 0,
    }),
    getSaveAnEmployeePayRollAdmin: builder.query<
      IApiPayrollResponse,
      ISavePayrollAnEmpPayload
    >({
      query: (request) => {
        return {
          url: "/save/punch/record/employee/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Payroll"],
      keepUnusedDataFor: 0,
    }),
    getSaveAnEmplEachRecordPayRollAdmin: builder.query<
      IApiPayrollResponse,
      ISavePayrollAnEmpEachRecordPayload
    >({
      query: (request) => {
        return {
          url: "/save/punch/record/employee/each",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Payroll"],
      keepUnusedDataFor: 0,
    }),
    getPunchDetailsEmp: builder.query<IApiResponse, IPayrollAnEmpPayload>({
      query: (request) => {
        return {
          url: "/punch/details/getEmp",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team"],
      keepUnusedDataFor: 0,
    }),
    getSaveEmployeePayrollAnEmployeeRecord: builder.query<
      IApiResponse,
      IPayrollAnEmpPayload
    >({
      query: (request) => {
        return {
          url: "/save/payroll/List/emp",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team"],
      keepUnusedDataFor: 0,
    }),
    PayrollRecordeEmp: builder.query<
      IApiPayrollAllResponse,
      IPayrollAnEmpPayloadSec
    >({
      query: (request) => {
        return {
          url: "/payroll/list/emp",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Payroll", "team"],
      keepUnusedDataFor: 0,
    }),
    EditEmployeePayRollAdmin: builder.query<IApiPayrollResponse, IGetRent>({
      query: (request) => {
        return {
          url: "/punch/record/edit/payroll-generate",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Payroll"],
      keepUnusedDataFor: 0,
    }),
    getDayRecordAnEmplEachRecord: builder.query<
      IApiPayrollResponse,
      IDayAnEmpEachRecordPayload
    >({
      query: (request) => {
        return {
          url: "/punch/record/employee/day/each",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Payroll"],
      keepUnusedDataFor: 0,
    }),
    empPayrollPayrollRecordReport: builder.query<
      IApiPayrollResponse,
      IDayEmpReportPayload
    >({
      query: (request) => {
        return {
          url: "/save/payroll/List/emp/day-by-day/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Payroll"],
      keepUnusedDataFor: 0,
    }),
    editPunchInTime: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/punch/edit/punch-in",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["PunchDetails"],
    }),
    editPunchOutTime: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/punch/edit/punch-out",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["PunchDetails"],
    }),
    createRescheduleUpdatedForAllDate: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/emp/schedule/record/for-all",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["Reschedule"],
    }),
    getEmpScheduleDetailsByDateRange: builder.query<
      IApiResponse,
      IPayrollPayloadWeeklyReport
    >({
      query: (request) => {
        return {
          url: "/employee/schedule/date/range",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["EmployeeSchedule", "PunchDetails"],
      keepUnusedDataFor: 0,
    }),
    createfixedPayrollGenerate: builder.mutation<
      IApiPayrollGenerateResponse,
      Object
    >({
      query: (request) => {
        return {
          url: "/punch/record/payroll-generate",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["Payroll"],
    }),
    getEmployeePayRollAdminUseEdit: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/edit/payroll",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["Payroll"],
    }),
    getDashboardFristSection: builder.query<IApiResponseDhashboard, void>({
      query: (request) => {
        return {
          url: "/dashboard/frist/section/getAll",
          method: "POST",
          body: request,
        };
      },
      keepUnusedDataFor: 1,
      providesTags: ["dashboard"],
    }),
    getDashboardSecSection: builder.query<IApiResponseDhashboard, void>({
      query: (request) => {
        return {
          url: "/dashboard/sec/section/getAll",
          method: "POST",
          body: request,
        };
      },
      keepUnusedDataFor: 1,
      providesTags: ["dashboard"],
    }),
    createEmail: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/email",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["email"],
    }),
    deleteEmail: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/email/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["email"],
    }),
    getEmail: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/email/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["email"],
      keepUnusedDataFor: 0,
    }),
    createEmailTemplates: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/email/templates",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["email"],
    }),
    deleteEmailTemplates: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/email/templates/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["email"],
    }),
    getEmailTemplates: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/email/templates/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["email"],
      keepUnusedDataFor: 0,
    }),
    createEmailRecord: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/email/record",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["email", "team"],
    }),
    deleteEmailRecord: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/email/record/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["email", "team"],
    }),
    getEmailRecord: builder.query<IApiResponse, IEmailRecordPayload>({
      query: (request) => {
        return {
          url: "/email/record/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["email", "team", "student"],
      keepUnusedDataFor: 0,
    }),
    createUserStaff: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/user/staff",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["User", "team"],
    }),
    deleteUserStaff: builder.mutation<
      IApiResponse,
      { id: string; staffId: string }
    >({
      query: ({ id, staffId }) => {
        return {
          url: `/user/staff/${id}/${staffId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["User", "team"],
    }),
    deleteAdminEditPunch: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/punch/details/admin/delete",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["PunchDetails"],
    }),
    getEmployeeScheduleDetailsByMonth: builder.query<
      IApiResponse,
      IPayrollPayload
    >({
      query: (request) => {
        return {
          url: "/employee/schedule/month",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["EmployeeSchedule", "PunchDetails"],
      keepUnusedDataFor: 0,
    }),
    getCheckAttananceStatus: builder.query<IApiResponse, IIdOnlyNumberPayload>({
      query: (request) => {
        return {
          url: "/attanance/read/recipt",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["EmployeeSchedule", "PunchDetails"],
      keepUnusedDataFor: 0,
    }),
    getEmpScheduleDetailsByDateDeleteRecord: builder.query<
      IApiResponse,
      IEmployeeScheduleDetailsPayload
    >({
      query: (request) => {
        return {
          url: "/employee/schedule/date/delete",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["EmployeeSchedule", "PunchDetails"],
      keepUnusedDataFor: 0,
    }),
    getOutOfLocation: builder.query<
      IApiResponse,
      IEmployeeScheduleDetailsPayload
    >({
      query: (request) => {
        return {
          url: "/punch/record/out-of",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["EmployeeSchedule", "PunchDetails"],
      keepUnusedDataFor: 0,
    }),
    getAllStaffDhashboardFrist: builder.query<
      IApiResponseDhashbordClient,
      IIdOnlyPayload
    >({
      query: (request) => {
        return {
          url: "/staff/dhashboard/frist",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team"],
      keepUnusedDataFor: 0,
    }),
    getAttananceStaffDhashboard: builder.query<
      IApiResponse,
      IPayrollAnEmpPayloadSec2
    >({
      query: (request) => {
        return {
          url: "/staff/dhashboard/attendance",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team"],
      keepUnusedDataFor: 0,
    }),
    getPayrollStaffDhashboard: builder.query<
      IApiResponse,
      IPayrollAnEmpPayloadSec
    >({
      query: (request) => {
        return {
          url: "/staff/dhashboard/payroll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team"],
      keepUnusedDataFor: 0,
    }),
    getAllDhashboardFrist: builder.query<
      IApiResponseDhashbordClient,
      IIdOnlyPayload
    >({
      query: (request) => {
        return {
          url: "/dhashboard/frist",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["dashboard"],
      keepUnusedDataFor: 0,
    }),
    getAttananceMainDhashboard: builder.query<IApiResponse, IPayrollEmployee>({
      query: (request) => {
        return {
          url: "/staff/main/dhashboard/attendance",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["dashboard"],
      keepUnusedDataFor: 0,
    }),
    getPayrollStaffMainDhashboard: builder.query<
      IApiResponse,
      IPayrollAnEmpPayload
    >({
      query: (request) => {
        return {
          url: "/staff/main/dhashboard/payroll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team"],
      keepUnusedDataFor: 0,
    }),
    getSmsRecord: builder.query<IApiResponse, IEmailRecordPayload>({
      query: (request) => {
        return {
          url: "/sms/record/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["email", "team", "student"],
      keepUnusedDataFor: 0,
    }),
    createStaffSmsRecord: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/sms/staff/record",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["email", "team"],
    }),
    resentSmsStaff: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/sms/staff/resent/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["email", "team"],
    }),
    createRequest: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/request",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["request"],
    }),
    deleteRequest: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/request/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["request"],
    }),
    getRequest: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/request/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["request"],
      keepUnusedDataFor: 0,
    }),
    resentEmailStaff: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/email/staff/resent/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["email", "team"],
    }),
    getEmployeeScheduleDetailsByDateOnlyEmploee: builder.query<
      IApiResponse,
      IEmployeeScheduleDetailsPayload2
    >({
      query: (request) => {
        return {
          url: "/employee/schedule/date/only/emp",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["EmployeeSchedule", "PunchDetails"],
      keepUnusedDataFor: 0,
    }),
    getResponedent: builder.query<IApiResponse, IEmailRecordPayload>({
      query: (request) => {
        return {
          url: "/request/respondent/id",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["request"],
      keepUnusedDataFor: 0,
    }),
    createRespontant: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/request/respondent",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["Reschedule", "request"],
    }),
    deleteRespontant: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/request/respondent/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Reschedule", "request"],
    }),
    getRequestNotification: builder.query<IApiResponse, IIdOnlyPayload>({
      query: (request) => {
        return {
          url: "/request/count",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["Reschedule", "request", "dashboard"],
      keepUnusedDataFor: 0,
    }),
    getRequestDrawerData: builder.query<IApiResponse, IIdOnlyPayload>({
      query: (request) => {
        return {
          url: "/request/data",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["request", "dashboard"],
      keepUnusedDataFor: 0,
    }),
    createRespontantUser: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/request/respondent/user",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["request", "dashboard"],
    }),
    getMonthActiveRequest: builder.query<IApiResponse, IPayrollPayload>({
      query: (request) => {
        return {
          url: "/month/request/active",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["request", "EmployeeSchedule"],
      keepUnusedDataFor: 0,
    }),
    updateDynamicSchedule: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/emp/schedule/record/dynamic",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["Reschedule", "request"],
    }),
    getMonthlyWorkDaysStaffDhashboard: builder.query<
      IApiResponse,
      IPayrollAnEmpPayload
    >({
      query: (request) => {
        return {
          url: "/staff/main/dhashboard/monthly/workdays",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team"],
      keepUnusedDataFor: 0,
    }),
    getEmployeeScheduleDetailsByMonthforEmp: builder.query<
      IApiResponse,
      IPayrollPayloadEmp
    >({
      query: (request) => {
        return {
          url: "/employee/schedule/month/emp",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["EmployeeSchedule", "PunchDetails"],
      keepUnusedDataFor: 0,
    }),
    getPayrollDhashboardData: builder.query<
      IApiResponseDhashbordClient,
      IPayrollPayloadEmpNull
    >({
      query: (request) => {
        return {
          url: "/staff/dhashboard/payroll/filter",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team"],
      keepUnusedDataFor: 0,
    }),
    createStudent: builder.mutation<IApiTenantResponse, Object>({
      query: (request) => {
        return {
          url: "/student",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["student"],
    }),
    // Billing-only save from the Payments tab Billing Setup panel. Uses
    // the dedicated backend endpoint so unrelated student columns
    // (parent, address, location, study_mode) cannot be nulled by an
    // incomplete payload — which is what happened when this panel
    // routed through the shared createStudent mutation.
    updateStudentBillingSetup: builder.mutation<IApiTenantResponse, { id: number | string; body: Object }>({
      query: ({ id, body }) => ({
        url: `/student/${id}/billing-setup`,
        method: "POST",
        body: JSON.stringify(body),
      }),
      invalidatesTags: ["student", "payment", "invoice"],
    }),
    deleteStudent: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/student/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["student"],
    }),
    getallStudent: builder.query<IApiResponse, IStudentListPayload>({
      query: (request) => {
        return {
          url: "/student/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student"],
      keepUnusedDataFor: 0,
    }),
    // Student activity timeline (persisted audit records; never fabricated).
    getStudentActivity: builder.query<
      IApiResponse,
      { studentId: string | number; page?: number; per_page?: number; module?: string; action_type?: string; actor?: string }
    >({
      query: ({ studentId, page = 1, per_page = 20, module, action_type, actor }) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("per_page", String(per_page));
        if (module) params.set("module", module);
        if (action_type) params.set("action_type", action_type);
        if (actor) params.set("actor", actor);
        return { url: `/students/${studentId}/activity?${params.toString()}`, method: "GET" };
      },
      providesTags: ["student"],
      keepUnusedDataFor: 0,
    }),
    // The student's recurring weekly class pattern (from active assignments).
    // Tagged so it refreshes when class/enrolment/schedule data changes.
    getStudentWeeklySchedule: builder.query<IApiResponse, { studentId: string | number }>({
      query: ({ studentId }) => ({ url: `/students/${studentId}/weekly-schedule`, method: "GET" }),
      providesTags: ["student", "class", "classSchedule"],
      keepUnusedDataFor: 30,
    }),
    // Occurrence-based, student-scoped calendar for the Student Profile → Calendar
    // tab. Reads class_occurrences (same source as Today's/Upcoming/Weekly) so the
    // sections can never disagree. Only the visible date range is fetched; RTK
    // Query dedupes concurrent calls and abandons superseded ones on rapid nav.
    getStudentCalendar: builder.query<
      IApiResponse,
      {
        studentId: string | number;
        start_date?: string;
        end_date?: string;
        subject_id?: string | number;
        staff_id?: string | number;
        class_type?: string;
        study_mode?: string;
        attendance_status?: string;
      }
    >({
      query: ({ studentId, ...params }) => {
        const search = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "") search.set(k, String(v));
        });
        const qs = search.toString();
        return { url: `/students/${studentId}/calendar${qs ? `?${qs}` : ""}`, method: "GET" };
      },
      providesTags: ["student", "class", "classSchedule", "attendanceRecord"],
      keepUnusedDataFor: 30,
    }),
    // One student-scoped occurrence for the shared class-details drawer. Keyed by
    // (studentId, occurrenceId) so RTK caches per-occurrence — opening a different
    // class never shows the previous one's data (stale-safe by construction).
    getStudentOccurrenceDetails: builder.query<IApiResponse, { studentId: string | number; occurrenceId: string | number }>({
      query: ({ studentId, occurrenceId }) => ({ url: `/students/${studentId}/occurrence/${occurrenceId}`, method: "GET" }),
      providesTags: ["student", "class", "classSchedule", "attendanceRecord"],
      keepUnusedDataFor: 30,
    }),
    createStudentperent: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/student/perent",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["student"],
    }),
    deleteStudentperent: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/student/perent/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["student"],
    }),
    getallStudentperent: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/student/perent/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student"],
      keepUnusedDataFor: 0,
    }),
    // Lightweight parent lookup for the Create Student page search box.
    // Backend requires q.length >= 3 and caps to `limit` rows (default 10),
    // so this must never be called for shorter terms — pass `skip` in the hook.
    lookupStudentperent: builder.query<IApiResponse, { q: string; limit?: number }>({
      query: (request) => ({
        url: "/student/perent/lookup",
        method: "POST",
        body: { q: request.q, limit: request.limit ?? 10 },
      }),
      providesTags: ["student"],
      keepUnusedDataFor: 0,
    }),

    // ── Parent Management module ──
    getAllParents: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => ({ url: "/parent/getAll", method: "POST", body: request }),
      providesTags: ["parent"],
      keepUnusedDataFor: 0,
    }),
    showParent: builder.query<IApiResponse, string>({
      query: (id) => ({ url: `/parent/show/${id}`, method: "POST", body: {} }),
      providesTags: ["parent"],
      keepUnusedDataFor: 0,
    }),
    // Invoice History on the Parent Profile — returns { summary, invoices[] }
    // for every student linked to this parent. Backend excludes superseded /
    // voided / cancelled / deleted invoices so the list matches the Finance
    // list exactly.
    getParentInvoices: builder.query<IApiResponse, string>({
      query: (id) => ({ url: `/parent/${id}/invoices`, method: "POST", body: {} }),
      providesTags: ["parent", "invoice"],
    }),
    createParent: builder.mutation<IApiResponse, Object>({
      query: (request) => ({ url: "/parent", method: "POST", body: JSON.stringify(request) }),
      invalidatesTags: ["parent", "student"],
    }),
    updateParent: builder.mutation<IApiResponse, { id: string; body: Object }>({
      query: ({ id, body }) => ({ url: `/parent/${id}`, method: "POST", body: JSON.stringify(body) }),
      invalidatesTags: ["parent", "student"],
    }),
    deleteParent: builder.mutation<IApiResponse, { id: string; mode?: "detach" | "delete" }>({
      query: ({ id, mode }) => ({
        // mode is sent as a query param so it survives DELETE requests reliably.
        url: mode ? `/parent/${id}?mode=${mode}` : `/parent/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["parent", "student"],
    }),
    toggleParentChildAccess: builder.mutation<IApiResponse, Object>({
      query: (request) => ({ url: "/parent/child-access", method: "POST", body: JSON.stringify(request) }),
      invalidatesTags: ["parent"],
    }),
    toggleParentAccess: builder.mutation<IApiResponse, Object>({
      query: (request) => ({ url: "/parent/toggle-access", method: "POST", body: JSON.stringify(request) }),
      invalidatesTags: ["parent"],
    }),
    removeParentChild: builder.mutation<IApiResponse, Object>({
      query: (request) => ({ url: "/parent/remove-child", method: "POST", body: JSON.stringify(request) }),
      invalidatesTags: ["parent", "student"],
    }),
    generateParentCredentials: builder.mutation<IApiResponse, void>({
      query: () => ({ url: "/parent/next-credentials", method: "POST", body: {} }),
    }),
    enableParentPortal: builder.mutation<IApiResponse, string | { id: string; mode?: "generate" | "manual"; new_password?: string; confirm_password?: string }>({
      query: (arg) => {
        const id = typeof arg === "string" ? arg : arg.id;
        const body = typeof arg === "string" ? {} : {
          mode: arg.mode ?? "generate",
          ...(arg.mode === "manual" ? { new_password: arg.new_password, confirm_password: arg.confirm_password } : {}),
        };
        return { url: `/parent/${id}/enable-portal`, method: "POST", body };
      },
      invalidatesTags: ["parent"],
    }),
    resetParentPassword: builder.mutation<IApiResponse, string | { id: string; mode?: "generate" | "manual"; new_password?: string; confirm_password?: string }>({
      query: (arg) => {
        const id = typeof arg === "string" ? arg : arg.id;
        const body = typeof arg === "string" ? {} : {
          mode: arg.mode ?? "generate",
          ...(arg.mode === "manual" ? { new_password: arg.new_password, confirm_password: arg.confirm_password } : {}),
        };
        return { url: `/parent/${id}/reset-password`, method: "POST", body };
      },
      invalidatesTags: ["parent"],
    }),
    sendParentLoginDetails: builder.mutation<IApiResponse, string>({
      query: (id) => ({ url: `/parent/${id}/send-login`, method: "POST", body: {} }),
    }),
    sendParentResetLink: builder.mutation<IApiResponse, string>({
      query: (id) => ({ url: `/parent/${id}/send-reset-link`, method: "POST", body: {} }),
    }),
    getParentPortalAccounts: builder.query<IApiResponse, { page?: number; per_page?: number; search?: string; status?: string }>({
      query: (r) => ({ url: "/parent-portal-management/list", method: "POST", body: r }),
      providesTags: ["parent"],
    }),
    getDashboardOperations: builder.query<IApiResponse, { location_id?: number | null }>({
      query: (r) => ({ url: "/dashboard/operations", method: "POST", body: r ?? {} }),
    }),
    getDashboardClassRoster: builder.query<IApiResponse, { occurrence_id: number }>({
      query: (r) => ({ url: "/dashboard/class-roster", method: "POST", body: r }),
    }),
    getDashboardFinanceSummary: builder.query<IApiResponse, { location_id?: number | null }>({
      query: (r) => ({ url: "/dashboard/finance-summary", method: "POST", body: r ?? {} }),
    }),

    createGrade: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/grade",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["grades"],
    }),
    deleteGrade: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/grade/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["grades"],
    }),
    getallGrade: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/grade/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["grades"],
      // See getTeam — dropdown lookup, tag-invalidated, safe to cache.
      keepUnusedDataFor: 600,
    }),
    getsearchStudentperent: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/student/perent/search",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student"],
      keepUnusedDataFor: 0,
    }),
    getallEnumSearch: builder.query<IApiResponse, ISearchEnumPayload>({
      query: (request) => {
        return {
          url: "/enum/search",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student", "grades"],
      keepUnusedDataFor: 0,
    }),
    getallEnum: builder.query<IApiResponse, ISearchEnumPayload>({
      query: (request) => {
        return {
          url: "/enum/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student", "grades"],
      keepUnusedDataFor: 0,
    }),
    createEnum: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/enum",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["grades", "student"],
    }),
    deleteEnum: builder.mutation<IApiResponse, number>({
      query: (Id) => {
        return {
          url: `/enum/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["grades", "student"],
    }),
    enumFindOrCreate: builder.mutation<IApiResponse, { enum_type: string; enum_value: string }>({
      query: (request) => ({
        url: "/enum/find-or-create",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["grades", "student"],
    }),
    getallLocation: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/location/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student", "location"],
      // See getTeam — dropdown lookup, tag-invalidated, safe to cache.
      keepUnusedDataFor: 600,
    }),
    createLocation: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/location",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["location", "student"],
    }),
    deleteLocation: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/location/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["location", "student"],
    }),
    getallSecoundGrade: builder.query<IApiResponse, ISearchGradePayload>({
      query: (request) => {
        return {
          url: "/grade/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["grades"],
      keepUnusedDataFor: 0,
    }),
    getallSubject: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/subject/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student", "subject", "grades", "team"],
      // See getTeam — dropdown lookup, tag-invalidated, safe to cache.
      keepUnusedDataFor: 600,
    }),
    createSubject: builder.mutation<IApiReceiptResponseIdInt, Object>({
      query: (request) => {
        return {
          url: "/subject",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["grades", "subject", "student", "team"],
    }),
    deleteSubject: builder.mutation<IApiResponse, number>({
      query: (Id) => {
        return {
          url: `/subject/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["grades", "subject", "student"],
    }),
    reorderSubjects: builder.mutation<IApiResponse, { ordered_ids: number[] }>({
      query: (request) => {
        return {
          url: "/subject/reorder",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["subject"],
    }),
    // Create a subject + link it to one or more grades in a single call.
    // Powers the "Create New Subject" modal on the Create/Edit Class page.
    // Returns grade_subjects[] so the caller can auto-select the new chips
    // without a page reload. Tags: subject/grades/team/class all consume the
    // subject-grade relationship, so invalidate together to refresh the
    // Subjects & Grades panel + any dependent staff eligibility caches.
    createSubjectWithGrades: builder.mutation<
      IApiResponse,
      {
        subject_name: string;
        subject_code?: string | null;
        description?: string | null;
        color?: string | null;
        is_active?: boolean;
        grade_ids: number[];
      }
    >({
      query: (request) => ({
        url: "/subject/create-with-grades",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["subject", "grades", "team", "class"],
    }),
    // ── Assessment Focus Areas ──
    getAssessmentFocusAreas: builder.query<IApiResponse, { search?: string }>({
      query: ({ search } = {}) => ({
        url: `/v1/assessment-focus-areas${search ? `?search=${encodeURIComponent(search)}` : ""}`,
        method: "GET",
      }),
      providesTags: ["focusArea"],
      keepUnusedDataFor: 0,
    }),
    createAssessmentFocusArea: builder.mutation<IApiResponse, Object>({
      query: (request) => ({
        url: "/v1/assessment-focus-areas",
        method: "POST",
        body: JSON.stringify(request),
      }),
      invalidatesTags: ["focusArea"],
    }),
    updateAssessmentFocusArea: builder.mutation<IApiResponse, { id: number; body: Object }>({
      query: ({ id, body }) => ({
        url: `/v1/assessment-focus-areas/${id}`,
        method: "PUT",
        body: JSON.stringify(body),
      }),
      invalidatesTags: ["focusArea"],
    }),
    deleteAssessmentFocusArea: builder.mutation<IApiResponse, number>({
      query: (id) => ({
        url: `/v1/assessment-focus-areas/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["focusArea"],
    }),
    reorderAssessmentFocusAreas: builder.mutation<IApiResponse, { items: { id: number; sort_order: number }[] }>({
      query: (request) => ({
        url: "/v1/assessment-focus-areas/reorder",
        method: "POST",
        body: JSON.stringify(request),
      }),
      invalidatesTags: ["focusArea"],
    }),
    createSubjectAddGrade: builder.mutation<IApiResponseUpdate, Object>({
      query: (request) => {
        return {
          url: "/subject/add-grade",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["grades", "subject", "student", "team"],
    }),
    createSubjectRemovedGrade: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/subject/removed-grade",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["grades", "subject", "student", "team"],
    }),
    getAllGradeCount: builder.query<ICountApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/grade/count",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["grades", "subject", "student", "team"],
      keepUnusedDataFor: 0,
    }),
    createSubjectGradeAssignStadd: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/subject-grade/assign/staff",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["team"],
    }),
    getStaffGradesAssignStaff: builder.query<
      ICountApiResponse,
      IEmployeePunchImgPayload
    >({
      query: (request) => {
        return {
          url: "/subject-grade/assign/staff/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team"],
      keepUnusedDataFor: 0,
    }),
    // Subject → Grades teaching assignments (Assign Teaching modal).
    getStaffSubjectAssignments: builder.query<ICountApiResponse, { id: number }>({
      query: (request) => {
        return {
          url: "/subject-grade/assign/staff/by-subject",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team"],
      keepUnusedDataFor: 0,
    }),
    saveStaffSubjectAssignments: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/subject-grade/assign/staff/by-subject/save",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["team"],
    }),
    // Atomic Add Subject — insert-only, never touches existing pairs.
    // Body: { staff_id, subject_id, grade_ids: number[] }
    addStaffTeachingSubject: builder.mutation<IApiResponse, { staff_id: number | string; subject_id: number; grade_ids: number[] }>({
      query: (request) => ({
        url: "/subject-grade/assign/staff/add-subject",
        method: "POST",
        body: request,
      }),
      // team = drawer / list / eligible staff pickers; class = Assign Class
      // eligibility. Both consume the staff↔grade↔subject relationship, so
      // invalidate together to keep them in sync without a browser refresh.
      invalidatesTags: ["team", "class", "classSchedule"],
    }),
    getGradesAssignStaff: builder.query<
      ICountApiResponse,
      IEmployeePunchImgPayload
    >({
      query: (request) => {
        return {
          url: "/grade/assign/staff-getall",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["grades", "team"],
      keepUnusedDataFor: 0,
    }),
    getsearchSatff: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/team/class/search",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student", "team", "class"],
      keepUnusedDataFor: 0,
    }),
    getStaffGradesAssignStaffGetPerent: builder.query<
      ICountApiResponse,
      IEmployeePunchImgPayload
    >({
      query: (request) => {
        return {
          url: "/subject-grade/assign/staff/perent",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team", "class"],
      keepUnusedDataFor: 0,
    }),
    assignClassesCreate: builder.mutation<IApiTenantResponse, Object>({
      query: (request) => {
        return {
          url: "/assign/classes",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      // Creating a class/template changes the template-backed calendars (class,
      // team) and — when students are enrolled at creation — materializes dated
      // occurrences, so refresh the occurrence-backed views (classSchedule) too.
      invalidatesTags: ["student", "class", "classSchedule", "team"],
    }),
    getAssignClassGetAllCaleder: builder.query<
      IApiResponse,
      IPayrollPayloadEmp
    >({
      query: (request) => {
        return {
          url: "/assign/classes/calender",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team", "class"],
      keepUnusedDataFor: 0,
    }),
    getallAssignClasses: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/subject-grade/assign/staff/calender/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student", "class", "team"],
      keepUnusedDataFor: 0,
    }),
    getAssignClassEmpGetAllCaleder: builder.query<
      IApiResponse,
      IPayrollPayloadEmp
    >({
      query: (request) => {
        return {
          url: "/assign/classes/calender/emp",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team", "class"],
      keepUnusedDataFor: 0,
    }),
    createTeamImage: builder.mutation<IApiTenantResponse, Object>({
      query: (request) => {
        return {
          url: "/team/img",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["team", "request"],
    }),
    createStudentImage: builder.mutation<IApiTenantResponse, Object>({
      query: (request) => {
        return {
          url: "/student/img",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["student"],
    }),
    getGradesAssignStudent: builder.query<
      ICountApiResponse,
      IEmployeePunchImgPayload
    >({
      query: (request) => {
        return {
          url: "/grade/assign/student-getall",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["grades", "student", "subject", "team", "class"],
      keepUnusedDataFor: 0,
    }),
    // Bulk-release a student from every class currently blocking
    // removal of ONE (grade, subject) enrollment. Backend recomputes
    // the authoritative blocking set. Invalidates the ClassSchedule
    // + student caches so any open Student Profile refreshes.
    releaseBlockingClassesForSubject: builder.mutation<
      IApiResponse, { studentId: number | string; pairId: number }
    >({
      query: ({ studentId, pairId }) => ({
        url: `/student/${studentId}/subject-enrollment/${pairId}/release-blocking-classes`,
        method: "POST",
      }),
      invalidatesTags: ["classSchedule", "student", "attendanceRecord"],
    }),
    listBlockingClassesForSubject: builder.query<
      IApiResponse, { studentId: number | string; pairId: number }
    >({
      query: ({ studentId, pairId }) => ({
        url: `/student/${studentId}/subject-enrollment/${pairId}/blocking-classes`,
        method: "GET",
      }),
      providesTags: (_r, _e, arg) => [
        { type: "classSchedule" as const, id: `sub-block-${arg.pairId}` },
      ],
      keepUnusedDataFor: 0,
    }),
    createSubjectGradeAssignStudentAdd: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/subject-grade/assign/student",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["student"],
    }),
    getStaffGradesAssignStudent: builder.query<
      ICountApiResponse,
      IEmployeePunchImgPayload
    >({
      query: (request) => {
        return {
          url: "/subject-grade/assign/student/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student"],
      keepUnusedDataFor: 0,
    }),
    createEmailStudentRecord: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/email/student/record",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["email", "student"],
    }),
    createStudentSmsRecord: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/sms/student/record",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["email", "student"],
    }),
    resentSmsStudent: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/sms/student/resent/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["email", "student"],
    }),
    resentEmailStudent: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/email/student/resent/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["email", "student"],
    }),
    // ── Parent Communication (Email + SMS) ─────────────────────────────
    getParentEmailRecords: builder.query<IApiResponse, { parent_id: number | string; page?: number; perPage?: number }>({
      query: (body) => ({
        url: "/email/parent/getAll",
        method: "POST",
        body: JSON.stringify(body),
      }),
      providesTags: ["email", "parent"],
      keepUnusedDataFor: 0,
    }),
    createParentEmailRecord: builder.mutation<IApiResponse, Object>({
      query: (request) => ({
        url: "/email/parent/record",
        method: "POST",
        body: JSON.stringify(request),
      }),
      invalidatesTags: ["email", "parent"],
    }),
    resentEmailParent: builder.mutation<IApiResponse, string>({
      query: (Id) => ({
        url: `/email/parent/resent/${Id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["email", "parent"],
    }),
    getParentSmsRecords: builder.query<IApiResponse, { parent_id: number | string; page?: number; perPage?: number }>({
      query: (body) => ({
        url: "/sms/parent/getAll",
        method: "POST",
        body: JSON.stringify(body),
      }),
      providesTags: ["email", "parent"],
      keepUnusedDataFor: 0,
    }),
    createParentSmsRecord: builder.mutation<IApiResponse, Object>({
      query: (request) => ({
        url: "/sms/parent/record",
        method: "POST",
        body: JSON.stringify(request),
      }),
      invalidatesTags: ["email", "parent"],
    }),
    resentSmsParent: builder.mutation<IApiResponse, string>({
      query: (Id) => ({
        url: `/sms/parent/resent/${Id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["email", "parent"],
    }),
    resetStudentperent: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/student/reset-perent",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["student"],
    }),
    switchPrimaryParent: builder.mutation<IApiResponse, { student_id: number; relationship_id: number }>({
      query: (request) => {
        return {
          url: "/student/switch-primary-parent",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["student"],
    }),
    // Student lifecycle — Drop / Reactivate.
    // Both write a `students.status` change + an activity_logs entry
    // via StudentActivityLogger so the profile History tab renders
    // Active → Dropped / Dropped → Active transitions automatically.
    dropStudent: builder.mutation<IApiResponse, { student_id: number; drop_start_date: string; drop_reason: string }>({
      query: ({ student_id, ...body }) => ({
        url: `/student/${student_id}/drop`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["student"],
    }),
    reactivateStudent: builder.mutation<IApiResponse, { student_id: number; reactivation_date: string; reason: string }>({
      query: ({ student_id, ...body }) => ({
        url: `/student/${student_id}/reactivate`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["student"],
    }),
    toggleStaffPortalAccess: builder.mutation<IApiResponse, { team_id: number; portal_access_enabled: boolean; portal_email?: string; portal_role_id?: number }>({
      query: (request) => ({
        url: "/team/toggle-portal",
        method: "POST",
        body: JSON.stringify(request),
      }),
      invalidatesTags: ["team"],
    }),
    // ── Staff Portal Access — spec §17 endpoints ─────────────────
    getStaffPortalAccess: builder.query<IApiResponse, { staffId: number | string }>({
      query: ({ staffId }) => ({ url: `/staff/${staffId}/portal-access`, method: "GET" }),
      providesTags: ["team"],
      keepUnusedDataFor: 5,
    }),
    createStaffPortalAccount: builder.mutation<
      IApiResponse,
      {
        staffId: number | string;
        portal_email?: string;
        portal_role_id?: number;
        send_invitation?: boolean;
        // Same delivery contract as sendStaffPasswordReset. Already
        // forwarded via the `...body` spread below, but the type is
        // widened so TS callers can pass these without an `any` cast.
        credential_delivery?: "email" | "display";
        password_mode?: "auto" | "manual";
        password?: string;
      }
    >({
      query: ({ staffId, ...body }) => ({ url: `/staff/${staffId}/portal-account`, method: "POST", body: JSON.stringify(body) }),
      invalidatesTags: ["team"],
    }),
    sendStaffPortalInvitation: builder.mutation<IApiResponse, { staffId: number | string }>({
      query: ({ staffId }) => ({ url: `/staff/${staffId}/portal-invitation`, method: "POST", body: JSON.stringify({}) }),
      invalidatesTags: ["team"],
    }),
    sendStaffPasswordReset: builder.mutation<
      IApiResponse,
      {
        staffId: number | string;
        // 'email' (default; email throttle applies) OR 'display'
        // (admin-visible one-time password; NO email, NO throttle).
        credential_delivery?: "email" | "display";
        // Only meaningful when credential_delivery === 'display'.
        password_mode?: "auto" | "manual";
        password?: string;
      }
    >({
      query: ({ staffId, ...body }) => ({
        url: `/staff/${staffId}/password-reset`,
        method: "POST",
        // Previous version hardcoded `JSON.stringify({})`, which
        // silently dropped credential_delivery + password_mode +
        // password on the floor. That defaulted the backend into
        // the email branch and triggered the "reset email was sent
        // recently" throttle even when the admin picked
        // "Choose password manually" — the bug this fix resolves.
        body: JSON.stringify(body),
      }),
      invalidatesTags: ["team"],
    }),
    suspendStaffPortalAccess: builder.mutation<IApiResponse, { staffId: number | string }>({
      query: ({ staffId }) => ({ url: `/staff/${staffId}/portal-access/suspend`, method: "POST", body: JSON.stringify({}) }),
      invalidatesTags: ["team"],
    }),
    enableStaffPortalAccess: builder.mutation<IApiResponse, { staffId: number | string }>({
      query: ({ staffId }) => ({ url: `/staff/${staffId}/portal-access/enable`, method: "POST", body: JSON.stringify({}) }),
      invalidatesTags: ["team"],
    }),
    getParentPortalStatus: builder.query<IApiResponse, { parent_id: number }>({
      query: (r) => ({ url: "/student/perent/portal-status", method: "POST", body: JSON.stringify(r) }),
      providesTags: (_res, _err, arg) => [
        // Per-parent tag lets toggleParentPortalAccess invalidate just the
        // one row it changed, instead of the whole "student" cache.
        { type: "ParentPortalStatus" as const, id: arg.parent_id },
        "student",
      ],
      // Keep the cached status live for a full minute so navigating away and
      // back to a student — or opening the Parent tab a second time on a
      // student with multiple parents — doesn't re-fire N portal-status
      // requests. The mutation still invalidates on write, so this cannot
      // go stale after an admin action.
      keepUnusedDataFor: 60,
    }),
    toggleParentPortalAccess: builder.mutation<IApiResponse, { parent_id: number; portal_access_enabled: boolean }>({
      query: (request) => ({
        url: "/student/perent/toggle-portal",
        method: "POST",
        body: JSON.stringify(request),
      }),
      // Only invalidate the one parent's portal-status row. Previously used
      // the blanket "student" tag which triggered a refetch of every
      // student-tagged query on the page (profile, enrolled subjects, perf
      // summary, payments, calendar, …) for what is really a single-row
      // change — the visible "frozen switch" delay was a downstream effect.
      invalidatesTags: (_res, _err, arg) => [
        { type: "ParentPortalStatus" as const, id: arg.parent_id },
      ],
    }),
    createGradeChanegName: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/grade/change-name",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["grades"],
    }),
    createSubjectChangeName: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/subject/change-name",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["grades", "subject", "student", "team"],
    }),
    getAssignClassFilter: builder.query<IApiResponse, IClassAsiigenFilter>({
      query: (request) => {
        return {
          url: "/assign/classes/fiter",
          method: "POST",
          body: request,
        };
      },
      // `team` is included so a staff edit (which invalidates `team`) also
      // refreshes the Recommended Classes cache — the response now carries
      // the live staff name, so it must be refetched when the staff name
      // changes. Otherwise a stale response could still render the old name.
      providesTags: ["student", "class", "team"],
      keepUnusedDataFor: 0,
    }),
    assignClassStudent: builder.mutation<IApiTenantResponse, Object>({
      query: (request) => {
        return {
          url: "/classes/assign-student",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      // Assigning a student generates that student's dated occurrences, so every
      // occurrence-backed view (schedule list, calendar, student today/upcoming,
      // staff dashboard/timetable, parent portal) must refetch, plus the
      // template/staff calendars.
      invalidatesTags: ["student", "class", "classSchedule", "team"],
    }),
    // Multi-class assignment: { student_id, class_ids: number[], effective_start_date? }.
    // Backend loads each class independently, validates + conflict-checks, and
    // attaches atomically. Same broad invalidation as the single assign.
    assignClassStudentMulti: builder.mutation<IApiTenantResponse, Object>({
      query: (request) => {
        return {
          url: "/classes/assign-student-multi",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["student", "class", "classSchedule", "team"],
    }),
    // Per-class-billing assignment flow. `preview` computes the invoice
    // summary before submit (no writes); `withBilling` performs the atomic
    // assign-and-invoice. Body shape:
    //   { student_id, classes: [{ class_id, billing_type, fee_amount,
    //     billing_start_date, billing_day?, first_month_proration?,
    //     invoice_generation_preference? }, ...] }
    previewAssignClassStudentBilling: builder.mutation<IApiTenantResponse, Object>({
      query: (request) => ({
        url: "/classes/assign-student-billing/preview",
        method: "POST",
        body: JSON.stringify(request),
      }),
    }),
    assignClassStudentWithBilling: builder.mutation<IApiTenantResponse, Object>({
      query: (request) => ({
        url: "/classes/assign-student-billing",
        method: "POST",
        body: JSON.stringify(request),
      }),
      invalidatesTags: ["student", "class", "classSchedule", "team", "invoice", "invoiceItem"],
    }),
    // Per-assignment billing summary for the student profile's Class Billing
    // Summary panel. Returns one row per active enrolment with the current
    // fee (from student_class_fees) and next billing date.
    getStudentClassBilling: builder.query<IApiTenantResponse, string | number>({
      query: (studentId) => ({
        url: `/student/${studentId}/class-billing`,
        method: "GET",
      }),
      providesTags: ["student", "class", "invoice"],
    }),
    // Edit Billing: supersedes the active fee row rather than mutating it;
    // never rewrites already-issued invoices.
    updateStudentClassBilling: builder.mutation<IApiTenantResponse, { assignmentId: number; body: any }>({
      query: ({ assignmentId, body }) => ({
        url: `/student-class-billing/${assignmentId}`,
        method: "PUT",
        body: JSON.stringify(body),
      }),
      invalidatesTags: ["student", "class", "invoice"],
    }),
    getAssignClassStudentGetAllCaleder: builder.query<
      IApiResponse,
      IPayrollPayloadEmp
    >({
      query: (request) => {
        return {
          url: "/assign/classes/calender/student",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student", "class"],
      keepUnusedDataFor: 0,
    }),
    getAssignClassListFilter: builder.query<IApiResponse, IClassAsiigenFilter>({
      query: (request) => {
        return {
          url: "/assign/classes/list/fiter",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student", "class"],
      keepUnusedDataFor: 0,
    }),
    deleteClassfilterStudent: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/classes/assign-student/${Id}`,
          method: "DELETE",
        };
      },
      // Removing a student stops their future occurrences — refresh occurrence-
      // backed views and the template/staff calendars alongside student/class.
      invalidatesTags: ["student", "class", "classSchedule", "team"],
    }),
    getAssignClassHistories: builder.query<IApiResponse, IIdOnlyPayload>({
      query: (request) => {
        return {
          url: "/classes/histories",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student", "class"],
      keepUnusedDataFor: 0,
    }),
    deleteAssignClass: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/assign/classes/${Id}`,
          method: "DELETE",
        };
      },
      // Delete cascades to future class_occurrences + legacy class_schedules
      // (classesAssignController@destroy), so every occurrence-backed view
      // must refetch — matches the invalidation set that assignClassesUpdate
      // uses, since the server-side footprint is identical.
      invalidatesTags: [
        "student", "class", "classSchedule", "team", "attendanceRecord",
        "ClassScheduleDetails", "ClassSchedules", "ClassOccurrences",
        "StaffSchedules", "StudentSchedules",
      ],
    }),
    assignClassesUpdate: builder.mutation<IApiTenantResponse, Object>({
      query: (request) => {
        return {
          url: "/assign/classes/update",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      // Editing a class reconciles its future occurrences and day-rows, so
      // every occurrence-backed / calendar / summary / drawer view must
      // refetch: Class Sessions list + calendar, Staff Profile schedule,
      // Student Profile calendar, Class Schedules list, Full Class View,
      // Today, attendance, upcoming sessions, summary counts, drawers.
      invalidatesTags: [
        "student", "class", "classSchedule", "team", "attendanceRecord",
        "ClassScheduleDetails", "ClassSchedules", "ClassOccurrences",
        "StaffSchedules", "StudentSchedules",
      ],
    }),
    createStudentperentOnly: builder.mutation<IApiReceiptResponseIdInt, Object>(
      {
        query: (request) => {
          return {
            url: "/student/perent/create",
            method: "POST",
            body: JSON.stringify(request),
          };
        },
        invalidatesTags: ["student"],
      }
    ),
    // Link an existing parent-person to a student via the relationship pivot
    // only — never creates a parent row. Used when the Create Parent flow
    // detects an existing_parent_match and the admin confirms the link.
    linkExistingParent: builder.mutation<IApiResponse, { parent_id: number; student_id: number; relationship_type: string }>(
      {
        query: (request) => ({
          url: "/student/perent/link",
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["student"],
      }
    ),
    // Unlink a parent from a specific student — soft-deactivates the
    // relationship pivot only. Never deletes the parent-person or
    // affects other children of that parent. If the removed pivot was
    // the student's primary, the caller supplies either
    // `new_primary_relationship_id` (another live rel on the same
    // student) or `allow_no_primary=true` (only accepted when it's the
    // sole parent). Idempotent.
    removeParentFromStudent: builder.mutation<IApiResponse, {
      student_id: number;
      parent_id: number;
      new_primary_relationship_id?: number | null;
      allow_no_primary?: boolean;
    }>({
      query: (body) => ({
        url: "/student/perent/remove-from-student",
        method: "POST",
        body,
      }),
      invalidatesTags: ["student"],
    }),
    // Per-parent linked-children lookup for the Remove confirmation
    // dialog. Fired only when the dialog opens so unused cards cost
    // zero requests.
    getParentLinkedStudents: builder.query<IApiResponse, { parentId: number }>({
      query: ({ parentId }) => ({
        url: `/student/perent/${parentId}/linked-students`,
        method: "GET",
      }),
    }),
    // Swap the student's current parent link for another existing parent
    // person, atomically. Deactivates the current student↔parent pivot,
    // links the new parent-person, transfers the primary pointer if the
    // released row was primary. Parent-person rows and other children are
    // never touched. Backs the "Replace Link" branch of the UPDATE
    // conflict dialog on the Parent Edit card.
    replaceParentLink: builder.mutation<IApiResponse, { student_id: number; current_parent_id: number; new_parent_id: number; relationship_type: string }>(
      {
        query: (request) => ({
          url: "/student/perent/replace-link",
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["student"],
      }
    ),
    // Live duplicate check for the Create Parent modal (phone / email already
    // taken by an active parent). Read-only; a mutation so it can be triggered
    // imperatively on blur without RTK cache coupling.
    checkParentDuplicate: builder.mutation<IApiResponse, { contact_number?: string; email?: string; id?: number }>(
      {
        query: (request) => ({
          url: "/student/perent/check-duplicate",
          method: "POST",
          body: request,
        }),
      }
    ),
    changeUserPasswordSystem: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/user/system/change-password",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["User"],
    }),
    getBackgroundImgandLogo: builder.query<IApiResponse, void>({
      query: (request) => {
        return {
          url: "/user/logo-bg",
          method: "POST",
          body: request,
        };
      },
      keepUnusedDataFor: 1,
      providesTags: ["User"],
    }),
    getallStudentByClass: builder.query<IApiResponse, IStudentClassPayload>({
      query: (request) => {
        return {
          url: "/student/by-class",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student", "class"],
      keepUnusedDataFor: 0,
    }),
    getClassSubjectByStaff: builder.query<
      ICountApiResponse,
      IEmployeePunchImgPayload
    >({
      query: (request) => {
        return {
          url: "/subject-grade/assign/staff-class",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team", "class"],
      keepUnusedDataFor: 0,
    }),
    getStaffGradesAssignStudentByGrade: builder.query<
      ICountApiResponse,
      IEmployeePunchImgPayload
    >({
      query: (request) => {
        return {
          url: "/subject-grade/assign/student/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student"],
      keepUnusedDataFor: 0,
    }),
    getStaffGetAllGradeBySubject: builder.query<
      ICountApiResponse,
      IEmployeePunchImgPayload
    >({
      query: (request) => {
        return {
          url: "/subject-grade/assign/staff/all-grade",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["team"],
      keepUnusedDataFor: 0,
    }),
    getStudentSubjectByClass: builder.query<
      ICountApiResponse,
      IStudentSubjectByClass
    >({
      query: (request) => {
        return {
          url: "/student/class-grade",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student", "class"],
      keepUnusedDataFor: 0,
    }),
    getStudentByClass: builder.query<IApiResponse, IIdOnlyPayload>({
      query: (request) => {
        return {
          url: "/assign/classes/student-list",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["student", "class"],
      keepUnusedDataFor: 0,
    }),

    // Class Schedules
    getClassSchedule: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/class-schedule/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["classSchedule"],
    }),
    // Lightweight KPI aggregate for the list header cards. Split out of
    // getClassSchedule in Phase-1 so paging the list doesn't force
    // recomputing every KPI.
    getClassScheduleSummary: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/class-schedule/summary",
        method: "POST",
        body: request,
      }),
      providesTags: ["classSchedule"],
    }),
    createClassSchedule: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/class-schedule",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["classSchedule"],
    }),
    // Single occurrence for the Edit page (GET /class-schedule/{id}).
    getOneClassSchedule: builder.query<IApiResponse, string | number>({
      query: (id) => ({
        url: `/class-schedule/${id}`,
        method: "GET",
      }),
      providesTags: ["classSchedule"],
      keepUnusedDataFor: 0,
    }),
    // Full occurrence-level detail for the View Details drawer / expanded row.
    getScheduleDetails: builder.query<IApiResponse, string | number>({
      query: (occurrenceId) => ({
        url: `/class-schedule/${occurrenceId}/details`,
        method: "GET",
      }),
      providesTags: ["classSchedule"],
      keepUnusedDataFor: 30,
    }),
    // Flat occurrence read for the Class Session edit form. Uses the new
    // /edit-form endpoint (class_occurrences) — the legacy /class-schedule/{id}
    // reads from class_schedules and 404s for occurrence IDs, which was the
    // "Schedule not found" bug on /class-sessions/:occurrenceId/edit.
    getOccurrenceEditForm: builder.query<IApiResponse, string | number>({
      query: (occurrenceId) => ({
        url: `/class-schedule/${occurrenceId}/edit-form`,
        method: "GET",
      }),
      providesTags: ["classSchedule"],
      keepUnusedDataFor: 0,
    }),
    // One-off / single-occurrence update. Same endpoint as create — store()
    // updates when an `id` is present.
    updateClassSchedule: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/class-schedule",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["classSchedule"],
    }),
    // Recurring-series scope edit: { scope: this|future|series, occurrence_id, class_id?, changes }
    seriesEditClassSchedule: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/class-schedule/series-edit",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["classSchedule"],
    }),
    generateClassSchedules: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/class-schedule/generate",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["classSchedule"],
    }),
    checkClassScheduleConflicts: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/class-schedule/conflicts",
        method: "POST",
        body: request,
      }),
    }),
    // Lazily materialize one class occurrence on demand (create-on-click for
    // dates beyond the rolling generation window). Payload: { classId, scheduledDate, studentId? }
    ensureClassOccurrence: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/class-schedule/ensure-occurrence",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["classSchedule"],
    }),
    deleteClassSchedule: builder.mutation<IApiResponse, string>({
      query: (id) => ({
        url: `/class-schedule/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["classSchedule"],
    }),

    // A staff switch changes who appears on the student's calendar and on the
    // attendance sheet, so it must invalidate the student/class/attendance views
    // too — not just the schedule and the staff roster.
    switchClassStaff: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/class-schedule/switch-staff", method: "POST", body: r }), invalidatesTags: ["classSchedule", "team", "student", "class", "attendanceRecord"] }),
    checkStaffAvailability: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/class-schedule/staff-availability", method: "POST", body: r }) }),
    bulkSwitchClassStaff: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/class-schedule/bulk-switch-staff", method: "POST", body: r }), invalidatesTags: ["classSchedule", "team", "student", "class", "attendanceRecord"] }),
    checkBulkStaffAvailability: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/class-schedule/bulk-staff-availability", method: "POST", body: r }) }),
    duplicateClass: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/class-schedule/duplicate", method: "POST", body: r }), invalidatesTags: ["classSchedule", "class", "student", "team"] }),
    assignStudentToClass: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/class-schedule/assign-student", method: "POST", body: r }), invalidatesTags: ["classSchedule", "class", "student"] }),
    // Releasing removes rows from every schedule view at once (list, calendars,
    // Today's/Weekly, All Classes) and can create a makeup occurrence, so it
    // invalidates the full schedule-dependent set.
    releaseClass: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/class-schedule/release", method: "POST", body: r }), invalidatesTags: ["classSchedule", "classCancellation", "student", "class", "team", "attendanceRecord"] }),
    // Student-level occurrence actions (occurrence id in the URL; never cancels the class).
    removeStudentFromOccurrence: builder.mutation<IApiResponse, any>({ query: ({ occurrenceId, ...body }) => ({ url: `/class-schedule/${occurrenceId}/remove-student`, method: "POST", body }), invalidatesTags: ["classSchedule", "student", "class", "attendanceRecord", "pendingMakeup"] }),
    // "Schedule Makeup Later" queue endpoints — see [[schedule-later]].
    getPendingMakeups: builder.query<IApiResponse, any>({ query: (params) => ({ url: "/class-schedule/pending-makeups", method: "GET", params }), providesTags: ["pendingMakeup"] }),
    markMakeupNotRequired: builder.mutation<IApiResponse, any>({ query: ({ id, ...body }) => ({ url: `/class-schedule/pending-makeups/${id}/not-required`, method: "POST", body }), invalidatesTags: ["pendingMakeup", "student"] }),
    // On-demand availability recompute (mutation ⇒ never stale after date/time changes).
    getMakeupOptions: builder.mutation<IApiResponse, any>({ query: ({ occurrenceId, ...body }) => ({ url: `/class-schedule/${occurrenceId}/makeup-options`, method: "POST", body }) }),
    scheduleMakeup: builder.mutation<IApiResponse, any>({ query: ({ occurrenceId, ...body }) => ({ url: `/class-schedule/${occurrenceId}/makeup`, method: "POST", body }), invalidatesTags: ["classSchedule", "student", "class", "team", "attendanceRecord", "pendingMakeup"] }),
    // Class-level replacement staff for a makeup slot (matching + genuinely available only).
    getReplacementStaff: builder.mutation<IApiResponse, any>({ query: ({ occurrenceId, ...body }) => ({ url: `/class-schedule/${occurrenceId}/replacement-staff`, method: "POST", body }) }),
    // Availability-aware, academically-suitable staff for Create/Edit Class Schedule.
    getClassScheduleAvailableStaff: builder.mutation<IApiResponse, any>({ query: (body) => ({ url: `/class-schedule/available-staff`, method: "POST", body }) }),

    // Class Cancellations
    getClassCancellation: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/class-cancellation/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["classCancellation"],
    }),
    createClassCancellation: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/class-cancellation",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["classCancellation", "classSchedule"],
    }),

    // Attendance Records
    getAttendanceRecord: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/attendance-record/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["attendanceRecord"],
    }),
    createAttendanceRecord: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/attendance-record",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["attendanceRecord"],
    }),
    getStudentAttendance: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/attendance-record/student",
        method: "POST",
        body: request,
      }),
      providesTags: ["attendanceRecord"],
    }),
    getClassAttendanceSummary: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/attendance-record/class-summary",
        method: "POST",
        body: request,
      }),
      providesTags: ["attendanceRecord"],
    }),
    getAttendanceReport: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/attendance-record/report",
        method: "POST",
        body: request,
      }),
      providesTags: ["attendanceRecord"],
    }),

    // Attendance Session (live per-subject "Mark Attendance" flow)
    startAttendanceSession: builder.mutation<IApiResponse, number>({
      query: (occurrenceId) => ({
        url: `/class-sessions/${occurrenceId}/attendance/start`,
        method: "POST",
      }),
      invalidatesTags: ["attendanceRecord"],
    }),
    getAttendanceSessionStudents: builder.query<IApiResponse, number>({
      query: (occurrenceId) => ({
        url: `/class-sessions/${occurrenceId}/attendance/students`,
        method: "GET",
      }),
      providesTags: ["attendanceRecord"],
    }),
    submitAttendanceSession: builder.mutation<
      IApiResponse,
      { occurrenceId: number; records: any[]; end_class?: boolean; end_source?: "manual" | "auto" }
    >({
      query: ({ occurrenceId, records, end_class, end_source }) => ({
        url: `/class-sessions/${occurrenceId}/attendance/submit`,
        method: "POST",
        // `end_source` (spec §7-8) rides along only when the caller
        // explicitly ends the class. Defaults to 'manual' server-side,
        // so any existing caller that omits it keeps its historical
        // ended_by='manual' stamp.
        body: {
          records,
          end_class: !!end_class,
          ...(end_class && end_source ? { end_source } : {}),
        },
      }),
      invalidatesTags: ["attendanceRecord"],
    }),
    lateArrivalAttendanceSession: builder.mutation<
      IApiResponse,
      {
        occurrenceId: number;
        student_id: number;
        subject_id: number | null;
        arrived_at: string;
        reason: string;
      }
    >({
      query: ({ occurrenceId, ...body }) => ({
        url: `/class-sessions/${occurrenceId}/attendance/late-arrival`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["attendanceRecord"],
    }),
    getLateStartsToday: builder.query<IApiResponse, void>({
      query: () => ({
        url: `/class-sessions/late-starts/today`,
        method: "GET",
      }),
      providesTags: ["attendanceRecord"],
    }),
    // Batch 11 — admin dashboard alerts (today).
    getAdminAlertsToday: builder.query<IApiResponse, void>({
      query: () => ({
        url: `/admin-alerts/today`,
        method: "GET",
      }),
      providesTags: ["attendanceRecord"],
    }),
    getClassNextSession: builder.query<IApiResponse, number>({
      query: (classId) => ({
        url: `/classes/${classId}/next-session`,
        method: "GET",
      }),
    }),
    getStaffTodaySession: builder.mutation<IApiResponse, number>({
      query: (staffId) => ({
        url: `/staff/${staffId}/today-session`,
        method: "GET",
      }),
    }),
    getClassSessionMessages: builder.query<
      IApiResponse,
      { occurrenceId: number; classId?: number; limit?: number }
    >({
      query: ({ occurrenceId, classId, limit }) => {
        const params = new URLSearchParams();
        if (classId) params.set("class_id", String(classId));
        if (limit) params.set("limit", String(limit));
        const qs = params.toString();
        return {
          url: `/class-sessions/${occurrenceId}/messages${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
    }),
    saveAttendanceSessionNote: builder.mutation<
      IApiResponse,
      { occurrenceId: number; student_id: number; note: string }
    >({
      query: ({ occurrenceId, ...body }) => ({
        url: `/class-sessions/${occurrenceId}/attendance/note`,
        method: "POST",
        body,
      }),
    }),
    getStudentAttendanceHistory: builder.query<
      IApiResponse,
      { studentId: number; limit?: number }
    >({
      query: ({ studentId, limit }) => ({
        url: `/students/${studentId}/attendance/history${limit ? `?limit=${limit}` : ""}`,
        method: "GET",
      }),
    }),
    getAttendanceReportList: builder.query<
      IApiResponse,
      {
        type?: "daily" | "weekly" | "monthly" | "custom";
        date?: string;
        from?: string;
        to?: string;
        staff_id?: string | number;
        grade?: string;
        student_id?: string | number;
      }
    >({
      query: (args) => {
        const params = new URLSearchParams();
        Object.entries(args || {}).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "" && v !== "all") {
            params.set(k, String(v));
          }
        });
        return {
          url: `/attendance/report?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["attendanceRecord"],
    }),
    // One-off lightweight roster for the Attendance Report → View Students
    // drawer. Historical (reads class_session_attendances), so a released
    // student still appears in the sessions they historically attended.
    getAttendanceSessionRoster: builder.query<IApiResponse, { sessionId: number | string }>({
      query: ({ sessionId }) => ({ url: `/attendance/report/${sessionId}/roster`, method: "GET" }),
    }),

    // Assessments
    getAssessments: builder.query<IApiResponse, Record<string, any>>({
      query: (args = {}) => {
        const params = new URLSearchParams();
        Object.entries(args || {}).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "" && v !== "all") {
            params.set(k, String(v));
          }
        });
        return { url: `/assessments?${params.toString()}`, method: "GET" };
      },
      providesTags: ["assessment" as any],
    }),
    getAssessmentStats: builder.query<IApiResponse, void>({
      query: () => ({ url: `/assessments/stats`, method: "GET" }),
      providesTags: ["assessment" as any],
    }),
    getAssessmentAnalytics: builder.query<IApiResponse, void>({
      query: () => ({ url: `/assessments/analytics`, method: "GET" }),
      providesTags: ["assessment" as any],
    }),
    getStudentDashboard: builder.query<IApiResponse, number | string>({
      query: (id) => ({ url: `/students/${id}/dashboard`, method: "GET" }),
      providesTags: ["student", "assessment" as any, "attendanceRecord"],
    }),
    getAssessmentFocuses: builder.query<IApiResponse, void>({
      query: () => ({ url: `/assessments/focuses`, method: "GET" }),
    }),
    createAssessment: builder.mutation<IApiResponse, any>({
      query: (body) => ({ url: `/assessments`, method: "POST", body }),
      invalidatesTags: ["assessment" as any],
    }),
    updateAssessment: builder.mutation<IApiResponse, { id: number; body: any }>({
      query: ({ id, body }) => ({ url: `/assessments/${id}`, method: "PUT", body }),
      invalidatesTags: ["assessment" as any],
    }),
    deleteAssessment: builder.mutation<IApiResponse, number>({
      query: (id) => ({ url: `/assessments/${id}`, method: "DELETE" }),
      invalidatesTags: ["assessment" as any],
    }),

    // ── Assessment cascade + groups (Batch 1 backend, wired here) ──
    // Cascades chain: staff → grades → subjects → students. Each call
    // scopes to the assignments of the caller/staff so a teacher only
    // ever sees their own students. The endpoints return
    // { data: [...], meta: { reason: '...' } } — `reason` is what the
    // wizard surfaces when a list is empty.
    getAssessmentCascadeGrades: builder.query<IApiResponse, { staff_id: number | string }>({
      query: ({ staff_id }) => ({ url: `/assessments/cascade/grades?staff_id=${encodeURIComponent(String(staff_id))}`, method: "GET" }),
      providesTags: ["assessment" as any],
    }),
    getAssessmentCascadeSubjects: builder.query<IApiResponse, { staff_id: number | string; grade_id: number | string }>({
      query: ({ staff_id, grade_id }) => ({
        url: `/assessments/cascade/subjects?staff_id=${encodeURIComponent(String(staff_id))}&grade_id=${encodeURIComponent(String(grade_id))}`,
        method: "GET",
      }),
      providesTags: ["assessment" as any],
    }),
    getAssessmentCascadeStudents: builder.query<IApiResponse, { staff_id: number | string; grade_id: number | string; subject_id: number | string }>({
      query: ({ staff_id, grade_id, subject_id }) => ({
        url: `/assessments/cascade/students?staff_id=${encodeURIComponent(String(staff_id))}&grade_id=${encodeURIComponent(String(grade_id))}&subject_id=${encodeURIComponent(String(subject_id))}`,
        method: "GET",
      }),
      providesTags: ["assessment" as any, "student"],
    }),
    createAssessmentGroup: builder.mutation<IApiResponse, any>({
      query: (body) => ({ url: `/assessments/groups`, method: "POST", body }),
      invalidatesTags: ["assessment" as any],
    }),
    publishAssessmentGroup: builder.mutation<IApiResponse, number | string>({
      query: (id) => ({ url: `/assessments/groups/${id}/publish`, method: "POST", body: {} }),
      invalidatesTags: ["assessment" as any],
    }),
    // Grading page (Batch 8) hooks — mechanically simple, added now so
    // the roster screen can consume them without another slice edit.
    getAssessmentGroupRoster: builder.query<IApiResponse, number | string>({
      query: (id) => ({ url: `/assessments/groups/${id}/roster`, method: "GET" }),
      providesTags: ["assessment" as any],
    }),
    bulkGradeAssessmentGroup: builder.mutation<
      IApiResponse,
      { id: number | string; rows: Array<Record<string, unknown>>; publish?: boolean }
    >({
      query: ({ id, rows, publish }) => ({
        url: `/assessments/groups/${id}/roster`,
        method: "PUT",
        body: { rows, publish: !!publish },
      }),
      invalidatesTags: ["assessment" as any],
    }),

    getAttendanceSessionsForDate: builder.query<
      IApiResponse,
      { from?: string; to?: string; date?: string; staff_id?: string | number }
    >({
      query: ({ from, to, date, staff_id }) => {
        const params = new URLSearchParams();
        if (from) params.set("from", from);
        if (to) params.set("to", to);
        if (date && !from && !to) params.set("date", date);
        if (staff_id && staff_id !== "all") params.set("staff_id", String(staff_id));
        return {
          url: `/attendance/sessions?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["attendanceRecord"],
    }),
    sendClassSessionMessage: builder.mutation<
      IApiResponse,
      {
        occurrenceId: number;
        recipients: "parents" | "admin" | "staff" | "all" | "both";
        student_ids: number[];
        subject: string;
        message: string;
        template?: string;
        template_id?: number;
      }
    >({
      query: ({ occurrenceId, ...body }) => ({
        url: `/class-sessions/${occurrenceId}/send-message`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["messageTemplate"],
    }),

    // Courses
    getCourse: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/course/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["course"],
    }),
    createCourse: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/course",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["course"],
    }),
    deleteCourse: builder.mutation<IApiResponse, string>({
      query: (id) => ({
        url: `/course/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["course"],
    }),

    // Course Materials
    getCourseMaterial: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/course-material/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["courseMaterial"],
    }),
    createCourseMaterial: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/course-material",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["courseMaterial"],
    }),
    // Multipart file upload for course attachments. Body must be a FormData
    // instance (RTK-Query passes it through when Content-Type is not set).
    uploadCourseMaterial: builder.mutation<IApiResponse, FormData>({
      query: (formData) => ({
        url: "/course-material/upload",
        method: "POST",
        body: formData,
        formData: true,
      }),
      invalidatesTags: ["courseMaterial", "course"],
    }),
    deleteCourseMaterial: builder.mutation<IApiResponse, string>({
      query: (id) => ({
        url: `/course-material/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["courseMaterial"],
    }),
    reorderCourseMaterial: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/course-material/reorder",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["courseMaterial"],
    }),

    // Assignments
    getAssignment: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/assignment/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["assignment"],
    }),
    createAssignment: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/assignment",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["assignment"],
    }),
    // Batch 13 — multi-student create backed by the assignment_students
    // pivot. Same shape as createAssessmentGroup: staff_id + grade_id +
    // subject_id + student_ids[] + assignment fields.
    createAssignmentAssignedToStudents: builder.mutation<IApiResponse, any>({
      query: (body) => ({
        url: "/assignments/assigned-to-students",
        method: "POST",
        body,
      }),
      invalidatesTags: ["assignment"],
    }),
    deleteAssignment: builder.mutation<IApiResponse, string>({
      query: (id) => ({
        url: `/assignment/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["assignment"],
    }),

    // Assignment Submissions
    getAssignmentSubmission: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/assignment-submission/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["assignmentSubmission"],
    }),
    createAssignmentSubmission: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/assignment-submission",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["assignmentSubmission"],
    }),
    gradeAssignmentSubmission: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/assignment-submission/grade",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["assignmentSubmission"],
    }),

    // Payment Methods
    getPaymentMethod: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/payment-method/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["paymentMethod"],
    }),
    createPaymentMethod: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/payment-method",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["paymentMethod"],
    }),
    deletePaymentMethod: builder.mutation<IApiResponse, string>({
      query: (id) => ({
        url: `/payment-method/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["paymentMethod"],
    }),
    setDefaultPaymentMethod: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/payment-method/default",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["paymentMethod"],
    }),

    // Invoices
    getInvoice: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/invoice/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["invoice"],
    }),
    createInvoice: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/invoice",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["invoice"],
    }),
    deleteInvoice: builder.mutation<IApiResponse, string>({
      query: (id) => ({
        url: `/invoice/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["invoice"],
    }),
    sendInvoice: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/invoice/send",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["invoice"],
    }),
    getOverdueInvoices: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/invoice/overdue",
        method: "POST",
        body: request,
      }),
      providesTags: ["invoice"],
    }),
    // ── Finance / Invoice rebuild endpoints ──
    // Summary card totals — respects the same filters the list uses so the
    // top of the page never disagrees with the rows below it.
    getInvoiceSummary: builder.query<IApiResponse, any>({
      query: (request) => ({ url: "/invoice/summary", method: "POST", body: request }),
      providesTags: ["invoice", "payment"],
    }),
    // Full detail — items + payments + parent + activity, secured by
    // FinanceScope::assertCanAccessInvoice on the backend.
    getInvoiceDetail: builder.query<IApiResponse, string | number>({
      query: (id) => ({ url: `/invoice/${id}/detail`, method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "invoice", id: String(id) }, "payment"],
    }),
    voidInvoice: builder.mutation<IApiResponse, { id: string | number; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/invoice/${id}/void`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: ["invoice", "payment"],
    }),
    duplicateInvoice: builder.mutation<IApiResponse, string | number>({
      query: (id) => ({ url: `/invoice/${id}/duplicate`, method: "POST" }),
      invalidatesTags: ["invoice"],
    }),
    markInvoiceSent: builder.mutation<IApiResponse, string | number>({
      query: (id) => ({ url: `/invoice/${id}/mark-sent`, method: "POST" }),
      invalidatesTags: ["invoice"],
    }),
    // Receipt: email the guardian (uses shared Blade template + Mail::html).
    emailReceipt: builder.mutation<IApiResponse, string | number>({
      query: (paymentId) => ({ url: `/receipt/${paymentId}/email`, method: "POST" }),
      invalidatesTags: ["invoice", "payment"],
    }),

    // Invoice Items
    createInvoiceItem: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/invoice-item",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["invoice", "invoiceItem"],
    }),
    deleteInvoiceItem: builder.mutation<IApiResponse, string>({
      query: (id) => ({
        url: `/invoice-item/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["invoice", "invoiceItem"],
    }),

    // Payments
    getPayment: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/payment/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["payment"],
    }),
    createPayment: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/payment",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["payment", "invoice"],
    }),
    getStudentPaymentHistory: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/payment/student-history",
        method: "POST",
        body: request,
      }),
      providesTags: ["payment"],
    }),
    getPaymentSummary: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/payment/summary",
        method: "POST",
        body: request,
      }),
      providesTags: ["payment"],
    }),

    // Notifications
    getNotification: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/notification/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["notification"],
    }),
    markNotificationAsRead: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/notification/read",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["notification"],
    }),
    getUnreadNotificationCount: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/notification/count",
        method: "POST",
        body: request,
      }),
      providesTags: ["notification"],
    }),

    // Messages
    getMessage: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/message/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["message"],
    }),
    createMessage: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/message",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["message"],
    }),
    getMessageThread: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/message/thread",
        method: "POST",
        body: request,
      }),
      providesTags: ["message"],
    }),
    markMessageAsRead: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/message/read",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["message"],
    }),
    deleteMessage: builder.mutation<IApiResponse, string>({
      query: (id) => ({
        url: `/message/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["message"],
    }),

    // Activity Logs
    getActivityLog: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/activity-log/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["activityLog"],
    }),

    // Permissions
    getPermissionMatrix: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/permission-matrix/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["permission"],
    }),
    getPermissionMatrixModules: builder.query<IApiResponse, void>({
      query: () => ({
        url: "/permission-matrix/modules",
        method: "POST",
        body: {},
      }),
      providesTags: ["permission"],
    }),
    createPermissionMatrix: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/permission-matrix",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["permission"],
    }),
    deletePermissionMatrix: builder.mutation<IApiResponse, string>({
      query: (id) => ({
        url: `/permission-matrix/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["permission"],
    }),
    getRolePermission: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/role-permission/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["rolePermission"],
    }),
    updateRolePermission: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/role-permission",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["rolePermission"],
    }),

    // Staff Availability
    getStaffAvailability: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/staff-availability/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["staffAvailability"],
    }),
    createStaffAvailability: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/staff-availability",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["staffAvailability"],
    }),
    deleteStaffAvailability: builder.mutation<IApiResponse, string>({
      query: (id) => ({
        url: `/staff-availability/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["staffAvailability"],
    }),
    getAvailableStaff: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/staff-availability/available",
        method: "POST",
        body: request,
      }),
      providesTags: ["staffAvailability"],
    }),

    // Student Notes
    getStudentNote: builder.query<IApiResponse, any>({
      query: (request) => ({
        url: "/student-note/getAll",
        method: "POST",
        body: request,
      }),
      providesTags: ["studentNote"],
    }),
    createStudentNote: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/student-note",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["studentNote"],
    }),
    deleteStudentNote: builder.mutation<IApiResponse, string>({
      query: (id) => ({
        url: `/student-note/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["studentNote"],
    }),
    toggleStudentNotePin: builder.mutation<IApiResponse, any>({
      query: (request) => ({
        url: "/student-note/pin",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["studentNote"],
    }),
    // Parent Portal Help — list of parent-visible published articles.
    // Backed by GET /api/parent-portal/help/articles (Sanctum-authed, no
    // internal HelpArticle_View permission needed — server always filters
    // to visible_to_parents=true regardless of caller identity).
    getParentHelpArticles: builder.query<IApiResponse, { category?: string } | void>({
      query: (arg) => {
        const q = arg?.category ? `?category=${encodeURIComponent(arg.category)}` : "";
        return { url: `/parent-portal/help/articles${q}`, method: "GET" };
      },
      providesTags: ["helpArticle"],
      keepUnusedDataFor: 60,
    }),
    getParentHelpArticle: builder.query<IApiResponse, { slug: string }>({
      query: ({ slug }) => ({ url: `/parent-portal/help/articles/${slug}`, method: "GET" }),
      providesTags: ["helpArticle"],
      keepUnusedDataFor: 60,
    }),
    // Health & Safety Notes modal — medical entries only. Backed by
    // student_notes where noteType='medical'. Route-gated by
    // StudentNote_View + data-scoped server-side.
    getStudentHealthNotes: builder.query<IApiResponse, { studentId: number | string }>({
      query: ({ studentId }) => ({
        url: `/students/${studentId}/health-notes`,
        method: "GET",
      }),
      providesTags: ["studentNote"],
      keepUnusedDataFor: 0,
    }),

    // Program Folders
    getProgramFolder: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/program-folder/getAll", method: "POST", body: r }), providesTags: ["programFolder"] }),
    // Batch 61 §2 — Treatment Areas (replaces the fixed programs.category enum).
    // Treatment areas — default excludes inactive rows so pickers do
    // not offer retired options for new assignment (server contract).
    // Settings-management callers pass `{ include_inactive: true }`
    // to see + reactivate inactive rows.
    getTreatmentAreas:   builder.query   <IApiResponse, void | { include_inactive?: boolean }>({
      query: (r) => ({
        url: "/aba/treatment-areas" + (r && (r as any).include_inactive ? "?include_inactive=1" : ""),
        method: "GET",
      }),
      providesTags: ["treatmentArea"],
    }),
    saveTreatmentArea:   builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/aba/treatment-areas", method: "POST", body: r }), invalidatesTags: ["treatmentArea", "program"] }),
    deleteTreatmentArea: builder.mutation<IApiResponse, number>({ query: (id) => ({ url: `/aba/treatment-areas/${id}`, method: "DELETE" }), invalidatesTags: ["treatmentArea", "program"] }),
    reorderTreatmentAreas: builder.mutation<IApiResponse, { order: number[] }>({ query: (body) => ({ url: "/aba/treatment-areas/reorder", method: "POST", body }), invalidatesTags: ["treatmentArea"] }),
    // Measurement Definitions — hybrid custom-label layer on top of the
    // closed recorder-type enum. See MeasurementDefinitionController.
    getMeasurementDefinitions: builder.query<IApiResponse, void | { include_inactive?: boolean }>({
      query: (r) => ({
        url: "/aba/measurement-definitions" + (r && (r as any).include_inactive ? "?include_inactive=1" : ""),
        method: "GET",
      }),
      providesTags: ["measurementDefinition"],
    }),
    saveMeasurementDefinition: builder.mutation<IApiResponse, { name: string; recorder_type: string }>({
      query: (body) => ({ url: "/aba/measurement-definitions", method: "POST", body }),
      invalidatesTags: ["measurementDefinition", "program"],
    }),
    createProgramFolder: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/program-folder", method: "POST", body: r }), invalidatesTags: ["programFolder"] }),
    // Batch 30 — send a body so the strategy-aware backend can dispatch.
    deleteProgramFolder: builder.mutation<IApiResponse, { id: number | string; strategy?: string; confirm?: boolean }>({
      query: ({ id, ...body }) => ({ url: `/program-folder/${id}`, method: "DELETE", body }),
      invalidatesTags: ["programFolder", "program"],
    }),
    moveProgramToFolder: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/program-folder/move-program", method: "POST", body: r }), invalidatesTags: ["programFolder", "program"] }),
    moveFolder: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/program-folder/move-folder", method: "POST", body: r }), invalidatesTags: ["programFolder", "program"] }),

    // Programs
    getProgram: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/program/getAll", method: "POST", body: r }), providesTags: ["program"] }),
    createProgram: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/program", method: "POST", body: r }), invalidatesTags: ["program"] }),
    deleteProgram: builder.mutation<IApiResponse, string>({ query: (id) => ({ url: `/program/${id}`, method: "DELETE" }), invalidatesTags: ["program"] }),
    updateProgramStatus: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/program/status", method: "POST", body: r }), invalidatesTags: ["program"] }),
    cloneProgram: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/program/clone", method: "POST", body: r }), invalidatesTags: ["program", "programFolder"] }),
    // Batch 1A §1A.5 — lock/unlock endpoints for the 7th kebab item.
    // Backend endpoints ship from Batch 114 (isLocked column + lock/unlock
    // controller). This is the only quarantined feature the row kebab now
    // depends on and it enters production via the Batch 1A build-out.
    lockProgram: builder.mutation<IApiResponse, string | number>({ query: (id) => ({ url: `/program/${id}/lock`, method: "POST" }), invalidatesTags: ["program"] }),
    unlockProgram: builder.mutation<IApiResponse, string | number>({ query: (id) => ({ url: `/program/${id}/unlock`, method: "POST" }), invalidatesTags: ["program"] }),
    // Batch 29: batch copy one template to N clients + server-side search
    // for the picker.
    copyProgramToClients: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/program/copy-to-clients", method: "POST", body: r }), invalidatesTags: ["program", "programFolder"] }),
    programStudentSearch: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/program/student-search", method: "POST", body: r }), providesTags: [] }),
    // Batch 32 — Program Details tabs + Session Data dashboard
    getProgramStudents: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/program/students", method: "POST", body: r }), providesTags: ["program"] }),
    getProgramProgress: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/program/progress", method: "POST", body: r }), providesTags: ["program", "sessionData"] }),
    getProgramHistory:  builder.query<IApiResponse, any>({ query: (r) => ({ url: "/program/history",  method: "POST", body: r }), providesTags: ["program"] }),
    getSessionDataList: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/session-data/list", method: "POST", body: r }), providesTags: ["sessionData"] }),
    getSessionDataKpis: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/session-data/kpis", method: "POST", body: r ?? {} }), providesTags: ["sessionData"] }),
    getProgramByStudent: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/program/by-student", method: "POST", body: r }), providesTags: ["program"] }),
    getProgramLibraryMetrics: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/program/library-metrics", method: "POST", body: r ?? {} }), providesTags: ["program", "programFolder", "promptTemplate", "masteryWorkflow", "sessionData"] }),

    // Program Targets
    getProgramTarget: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/program-target/getAll", method: "POST", body: r }), providesTags: ["programTarget"] }),
    createProgramTarget: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/program-target", method: "POST", body: r }), invalidatesTags: ["programTarget"] }),
    bulkImportTargets: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/program-target/bulk-import", method: "POST", body: r }), invalidatesTags: ["programTarget"] }),
    // Batch 45 §4 — duplicate one target within its program via /{id}/duplicate.
    duplicateProgramTarget: builder.mutation<IApiResponse, string | number>({ query: (id) => ({ url: `/program-target/${id}/duplicate`, method: "POST" }), invalidatesTags: ["programTarget"] }),
    reorderProgramTargets: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/program-target/reorder", method: "POST", body: r }), invalidatesTags: ["programTarget"] }),
    updateTargetSD: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/program-target/update-sd", method: "POST", body: r }), invalidatesTags: ["programTarget"] }),
    updateTargetStatus: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/program-target/status", method: "POST", body: r }), invalidatesTags: ["programTarget"] }),
    // Batch 34 §2.4 — bulk operations for the target-list checkbox bar.
    bulkTargetStatus: builder.mutation<IApiResponse, { targetIds: Array<string|number>; status: string }>({ query: (r) => ({ url: "/program-target/bulk-status", method: "POST", body: r }), invalidatesTags: ["programTarget"] }),
    bulkTargetDelete: builder.mutation<IApiResponse, { targetIds: Array<string|number> }>({ query: (r) => ({ url: "/program-target/bulk-delete", method: "POST", body: r }), invalidatesTags: ["programTarget"] }),
    deleteProgramTarget: builder.mutation<IApiResponse, string>({ query: (id) => ({ url: `/program-target/${id}`, method: "DELETE" }), invalidatesTags: ["programTarget"] }),

    // Prompt Templates
    getPromptTemplate: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/prompt-template/getAll", method: "POST", body: r }), providesTags: ["promptTemplate"] }),
    createPromptTemplate: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/prompt-template", method: "POST", body: r }), invalidatesTags: ["promptTemplate"] }),
    deletePromptTemplate: builder.mutation<IApiResponse, string>({ query: (id) => ({ url: `/prompt-template/${id}`, method: "DELETE" }), invalidatesTags: ["promptTemplate"] }),
    // Batch 33 — Prompt Hierarchies workspace
    listPromptTemplates: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/prompt-template/list", method: "POST", body: r }), providesTags: ["promptTemplate"] }),
    getPromptTemplateById: builder.query<IApiResponse, string | number>({ query: (id) => ({ url: `/prompt-template/${id}`, method: "GET" }), providesTags: ["promptTemplate"] }),
    updatePromptTemplate: builder.mutation<IApiResponse, any>({ query: ({ id, ...body }) => ({ url: `/prompt-template/${id}`, method: "PUT", body }), invalidatesTags: ["promptTemplate"] }),
    duplicatePromptTemplate: builder.mutation<IApiResponse, string | number>({ query: (id) => ({ url: `/prompt-template/${id}/duplicate`, method: "POST" }), invalidatesTags: ["promptTemplate"] }),
    setDefaultPromptTemplate: builder.mutation<IApiResponse, string | number>({ query: (id) => ({ url: `/prompt-template/${id}/set-default`, method: "POST" }), invalidatesTags: ["promptTemplate"] }),
    archivePromptTemplate: builder.mutation<IApiResponse, string | number>({ query: (id) => ({ url: `/prompt-template/${id}/archive`, method: "POST" }), invalidatesTags: ["promptTemplate"] }),
    restorePromptTemplate: builder.mutation<IApiResponse, string | number>({ query: (id) => ({ url: `/prompt-template/${id}/restore`, method: "POST" }), invalidatesTags: ["promptTemplate"] }),
    reorderPromptLevels: builder.mutation<IApiResponse, { id: string | number; levelIds: Array<string | number> }>({ query: ({ id, levelIds }) => ({ url: `/prompt-template/${id}/reorder`, method: "POST", body: { levelIds } }), invalidatesTags: ["promptTemplate"] }),

    // Mastery Workflows
    getMasteryWorkflow: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/mastery-workflow/getAll", method: "POST", body: r }), providesTags: ["masteryWorkflow"] }),
    createMasteryWorkflow: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/mastery-workflow", method: "POST", body: r }), invalidatesTags: ["masteryWorkflow"] }),
    deleteMasteryWorkflow: builder.mutation<IApiResponse, string>({ query: (id) => ({ url: `/mastery-workflow/${id}`, method: "DELETE" }), invalidatesTags: ["masteryWorkflow"] }),
    // Batch 34 — Mastery Workflows workspace
    listMasteryWorkflows: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/mastery-workflow/list", method: "POST", body: r }), providesTags: ["masteryWorkflow"] }),
    getMasteryWorkflowById: builder.query<IApiResponse, string | number>({ query: (id) => ({ url: `/mastery-workflow/${id}`, method: "GET" }), providesTags: ["masteryWorkflow"] }),
    updateMasteryWorkflow: builder.mutation<IApiResponse, any>({ query: ({ id, ...body }) => ({ url: `/mastery-workflow/${id}`, method: "PUT", body }), invalidatesTags: ["masteryWorkflow"] }),
    duplicateMasteryWorkflow: builder.mutation<IApiResponse, string | number>({ query: (id) => ({ url: `/mastery-workflow/${id}/duplicate`, method: "POST" }), invalidatesTags: ["masteryWorkflow"] }),
    setDefaultMasteryWorkflow: builder.mutation<IApiResponse, string | number>({ query: (id) => ({ url: `/mastery-workflow/${id}/set-default`, method: "POST" }), invalidatesTags: ["masteryWorkflow"] }),
    archiveMasteryWorkflow: builder.mutation<IApiResponse, string | number>({ query: (id) => ({ url: `/mastery-workflow/${id}/archive`, method: "POST" }), invalidatesTags: ["masteryWorkflow"] }),
    restoreMasteryWorkflow: builder.mutation<IApiResponse, string | number>({ query: (id) => ({ url: `/mastery-workflow/${id}/restore`, method: "POST" }), invalidatesTags: ["masteryWorkflow"] }),
    reorderMasteryPhases: builder.mutation<IApiResponse, { id: string | number; phaseIds: Array<string | number> }>({ query: ({ id, phaseIds }) => ({ url: `/mastery-workflow/${id}/reorder`, method: "POST", body: { phaseIds } }), invalidatesTags: ["masteryWorkflow"] }),

    // Batch 7C Step 2 — phase intervals (stacked maintenance schedules).
    listPhaseIntervals:   builder.query<IApiResponse, string | number>({ query: (phaseId) => ({ url: `/mastery-workflow-phase-interval/${phaseId}`, method: "GET" }), providesTags: ["masteryWorkflow"] }),
    createPhaseInterval:  builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/mastery-workflow-phase-interval", method: "POST", body: r }), invalidatesTags: ["masteryWorkflow"] }),
    updatePhaseInterval:  builder.mutation<IApiResponse, { id: string | number } & any>({ query: ({ id, ...body }) => ({ url: `/mastery-workflow-phase-interval/${id}`, method: "PUT", body }), invalidatesTags: ["masteryWorkflow"] }),
    reorderPhaseIntervals: builder.mutation<IApiResponse, { phaseId: string | number; intervalIds: Array<string | number> }>({ query: ({ phaseId, intervalIds }) => ({ url: `/mastery-workflow-phase-interval/${phaseId}/reorder`, method: "POST", body: { intervalIds } }), invalidatesTags: ["masteryWorkflow"] }),
    deletePhaseInterval:  builder.mutation<IApiResponse, string | number>({ query: (id) => ({ url: `/mastery-workflow-phase-interval/${id}`, method: "DELETE" }), invalidatesTags: ["masteryWorkflow"] }),

    // Aug 2026 — Supervisor Approval queue.
    listMasteryApprovals:   builder.query<IApiResponse, { status?: string; targetId?: number; programId?: number; search?: string } | void>({ query: (r) => ({ url: "/mastery-approvals", method: "GET", params: r ?? {} }), providesTags: ["masteryApproval"] }),
    approveMasteryApproval: builder.mutation<IApiResponse, { id: number; notes?: string }>({ query: ({ id, notes }) => ({ url: `/mastery-approvals/${id}/approve`, method: "POST", body: { notes } }), invalidatesTags: ["masteryApproval", "programTarget"] }),
    rejectMasteryApproval:  builder.mutation<IApiResponse, { id: number; notes?: string }>({ query: ({ id, notes }) => ({ url: `/mastery-approvals/${id}/reject`, method: "POST", body: { notes } }), invalidatesTags: ["masteryApproval"] }),

    // Batch 10 §10.3 — per-program prompt-level visibility + order overrides.
    listProgramPromptLevelCustomizations: builder.query<IApiResponse, string | number>({ query: (programId) => ({ url: `/program/${programId}/prompt-level-customizations`, method: "GET" }), providesTags: ["program"] }),
    saveProgramPromptLevelCustomizations: builder.mutation<IApiResponse, { programId: string | number; items: Array<{ promptLevelId: number; customOrder: number; isHidden: boolean }> }>({ query: ({ programId, items }) => ({ url: `/program/${programId}/prompt-level-customizations`, method: "POST", body: { items } }), invalidatesTags: ["program"] }),

    // Session Data
    getSessionData: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/session-data/getAll", method: "POST", body: r }), providesTags: ["sessionData"] }),
    createSessionData: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/session-data", method: "POST", body: r }), invalidatesTags: ["sessionData", "programTarget"] }),
    getTargetProgress: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/session-data/progress", method: "POST", body: r }), providesTags: ["sessionData"] }),
    getStudentOverview: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/session-data/student-overview", method: "POST", body: r }), providesTags: ["sessionData"] }),

    // Batch 35 — Session Data Engine
    getSessionById: builder.query<IApiResponse, string | number>({ query: (id) => ({ url: `/session-data/${id}`, method: "GET" }), providesTags: ["sessionData"] }),
    getSessionDraftCurrent: builder.query<IApiResponse, { studentId?: number|string; targetId?: number|string; draftKey?: string }>({ query: (r) => ({ url: "/session-data/draft/current", method: "GET", params: r }), providesTags: ["sessionData"] }),
    sessionAutosave: builder.mutation<IApiResponse, { id?: number|string; [k: string]: any }>({ query: ({ id, ...body }) => ({ url: id ? `/session-data/${id}/autosave` : "/session-data/autosave", method: "POST", body }), invalidatesTags: ["sessionData"] }),
    sessionAddEntry: builder.mutation<IApiResponse, { id: number|string; [k: string]: any }>({ query: ({ id, ...body }) => ({ url: `/session-data/${id}/entries`, method: "POST", body }), invalidatesTags: ["sessionData"] }),
    sessionUpdateEntry: builder.mutation<IApiResponse, { id: number|string; entryId: number|string; [k: string]: any }>({ query: ({ id, entryId, ...body }) => ({ url: `/session-data/${id}/entries/${entryId}`, method: "PUT", body }), invalidatesTags: ["sessionData"] }),
    sessionDeleteEntry: builder.mutation<IApiResponse, { id: number|string; entryId: number|string }>({ query: ({ id, entryId }) => ({ url: `/session-data/${id}/entries/${entryId}`, method: "DELETE" }), invalidatesTags: ["sessionData"] }),
    sessionUndoEntry: builder.mutation<IApiResponse, number|string>({ query: (id) => ({ url: `/session-data/${id}/entries/undo-last`, method: "POST" }), invalidatesTags: ["sessionData"] }),
    sessionComplete: builder.mutation<IApiResponse, number|string>({ query: (id) => ({ url: `/session-data/${id}/complete`, method: "POST" }), invalidatesTags: ["sessionData"] }),
    sessionSubmitReview: builder.mutation<IApiResponse, number|string>({ query: (id) => ({ url: `/session-data/${id}/submit-review`, method: "POST" }), invalidatesTags: ["sessionData"] }),
    sessionApprove: builder.mutation<IApiResponse, { id: number|string; note?: string }>({ query: ({ id, ...body }) => ({ url: `/session-data/${id}/approve`, method: "POST", body }), invalidatesTags: ["sessionData"] }),
    sessionReturn: builder.mutation<IApiResponse, { id: number|string; reason: string }>({ query: ({ id, ...body }) => ({ url: `/session-data/${id}/return`, method: "POST", body }), invalidatesTags: ["sessionData"] }),
    sessionVoid: builder.mutation<IApiResponse, { id: number|string; reason: string }>({ query: ({ id, ...body }) => ({ url: `/session-data/${id}/void`, method: "POST", body }), invalidatesTags: ["sessionData"] }),
    sessionRestore: builder.mutation<IApiResponse, { id: number|string; toStatus?: string }>({ query: ({ id, ...body }) => ({ url: `/session-data/${id}/restore`, method: "POST", body }), invalidatesTags: ["sessionData"] }),
    searchSessionStudents: builder.query<IApiResponse, { q: string }>({ query: (r) => ({ url: "/session-data/search/students", method: "GET", params: r }) }),
    searchSessionStaff:    builder.query<IApiResponse, { q: string; student_id?: number|string; program_id?: number|string }>({ query: (r) => ({ url: "/session-data/search/staff",    method: "GET", params: r }) }),
    searchSessionPrograms: builder.query<IApiResponse, { q?: string; studentId?: number|string }>({ query: (r) => ({ url: "/session-data/search/programs", method: "GET", params: r }) }),
    searchSessionTargets:  builder.query<IApiResponse, { q?: string; programId: number|string }>({ query: (r) => ({ url: "/session-data/search/targets", method: "GET", params: r }) }),

    // Batch 45 — Program bulk actions
    bulkMovePrograms:    builder.mutation<IApiResponse, { programIds: number[]; folderId: number | null }>({ query: (body) => ({ url: "/programs/bulk/move",    method: "POST", body }), invalidatesTags: ["program", "programFolder"] }),
    bulkStatusPrograms:  builder.mutation<IApiResponse, { programIds: number[]; status: string }>({ query: (body) => ({ url: "/programs/bulk/status",  method: "POST", body }), invalidatesTags: ["program"] }),
    bulkArchivePrograms: builder.mutation<IApiResponse, { programIds: number[] }>({ query: (body) => ({ url: "/programs/bulk/archive", method: "POST", body }), invalidatesTags: ["program"] }),
    bulkRestorePrograms: builder.mutation<IApiResponse, { programIds: number[] }>({ query: (body) => ({ url: "/programs/bulk/restore", method: "POST", body }), invalidatesTags: ["program"] }),

    // Batch 44 — Target import wizard + Today's ABA Sessions
    previewTargetImport: builder.mutation<IApiResponse, any>({ query: (body) => ({ url: "/aba/targets/import/preview", method: "POST", body }) }),
    commitTargetImport:  builder.mutation<IApiResponse, any>({ query: (body) => ({ url: "/aba/targets/import/commit",  method: "POST", body }), invalidatesTags: ["programTarget"] }),
    getTodayAbaSessions: builder.query<IApiResponse, { date?: string }>({ query: (r) => ({ url: "/aba/sessions/today", method: "GET", params: r }), providesTags: ["sessionData"] }),

    // T63 Part O — DB-backed workflow objective catalogue. GET list
    // returns system + custom active objectives; POST creates a custom.
    listWorkflowObjectives: builder.query<IApiResponse, void>({
      query: () => ({ url: "/workflow-objectives", method: "GET" }),
      providesTags: ["workflowObjective"],
    }),
    createWorkflowObjective: builder.mutation<IApiResponse, {
      name: string; category: string; direction: string; aggregationType: string; description?: string;
    }>({
      query: (body) => ({ url: "/workflow-objectives", method: "POST", body }),
      invalidatesTags: ["workflowObjective"],
    }),
    // Mark today's session as not-held (writes status='cancelled'). Invalidates
    // sessionData so the today card disappears without a reload.
    cancelTodaySession: builder.mutation<IApiResponse, { studentId: number|string; programId: number|string; staffId?: number|string|null }>({
      query: (body) => ({ url: "/aba/sessions/cancel", method: "POST", body }),
      invalidatesTags: ["sessionData"],
    }),
    // Discontinue a program-student assignment. Invalidates program (student
    // roster on the program view) and sessionData (today card).
    endProgramStudentAssignment: builder.mutation<IApiResponse, number | string>({
      query: (id) => ({ url: `/program-student-assignment/${id}/end`, method: "POST" }),
      invalidatesTags: ["program", "sessionData"],
    }),

    // Batch 38 — Students & Staff caseload workspace
    searchAbaStaff: builder.query<IApiResponse, { q?: string; student_id?: number|string; location_id?: number|string; role?: string; include_all_authorized?: boolean }>({
      query: (r) => ({ url: "/aba/staff/search", method: "GET", params: r }),
    }),
    listProgramAssignments: builder.query<IApiResponse, number | string>({
      query: (programId) => ({ url: `/aba/program-assignments/${programId}`, method: "GET" }),
      providesTags: ["program"],
    }),
    validateProgramAssignments: builder.mutation<IApiResponse, { program_id: number|string; assignments: any[] }>({
      query: (body) => ({ url: "/aba/program-assignments/validate", method: "POST", body }),
    }),
    saveProgramAssignments: builder.mutation<IApiResponse, { program_id: number|string; assignments: any[] }>({
      query: (body) => ({ url: "/aba/program-assignments", method: "POST", body }),
      invalidatesTags: ["program"],
    }),

    // Clients
    getClient: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/client/getAll", method: "POST", body: r }), providesTags: ["client"] }),
    createClient: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/client", method: "POST", body: r }), invalidatesTags: ["client"] }),
    getClientPrograms: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/client/programs", method: "POST", body: r }), providesTags: ["client", "program"] }),

    // Student Profile Data
    getStudentAttendanceSummary: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/attendance-record/student-summary", method: "POST", body: r }), providesTags: ["attendanceRecord", "student"] }),
    getStudentAttendanceCalendar: builder.query<IApiResponse, { studentId: number | string; month?: string }>({
      query: (r) => ({ url: "/attendance-record/student-calendar", method: "POST", body: r }),
      providesTags: ["attendanceRecord", "student"],
    }),
    getAttendanceSessionReport: builder.query<IApiResponse, { sessionId: number | string }>({
      query: ({ sessionId }) => ({ url: `/attendance/report/${sessionId}`, method: "GET" }),
      providesTags: ["attendanceRecord"],
    }),
    getStudentAssignments: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/student-profile/assignments", method: "POST", body: r }), providesTags: ["assignment", "student"] }),
    getStudentAssessmentList: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/student-profile/assessment-list", method: "POST", body: r }), providesTags: ["student"] }),
    getStudentPayments: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/student-profile/payments", method: "POST", body: r }), providesTags: ["payment", "invoice", "student"] }),
    getStudentPerformance: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/student-profile/performance", method: "POST", body: r }), providesTags: ["student", "attendanceRecord"] }),
    getStudentChallenges: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/student-profile/challenges", method: "POST", body: r }), providesTags: ["student", "studentNote"] }),
    storeStudentAssessment: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/student-profile/assessment", method: "POST", body: r }), invalidatesTags: ["student"] }),
    storeStudentChallenge: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/student-profile/challenge", method: "POST", body: r }), invalidatesTags: ["student", "studentNote"] }),
    storeStudentStrength: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/student-profile/strength", method: "POST", body: r }), invalidatesTags: ["student"] }),
    deleteStudentAssessment: builder.mutation<IApiResponse, string>({ query: (id) => ({ url: `/student-profile/assessment/${id}`, method: "DELETE" }), invalidatesTags: ["student"] }),
    deleteStudentChallenge: builder.mutation<IApiResponse, string>({ query: (id) => ({ url: `/student-profile/challenge/${id}`, method: "DELETE" }), invalidatesTags: ["student", "studentNote"] }),
    deleteStudentStrength: builder.mutation<IApiResponse, string>({ query: (id) => ({ url: `/student-profile/strength/${id}`, method: "DELETE" }), invalidatesTags: ["student"] }),

    // Staff Tutoring Dashboard
    getStaffDashboard: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/staff-dashboard/overview", method: "POST", body: r }), providesTags: ["team"] }),
    getStaffClassRecords: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/staff-dashboard/class-records", method: "POST", body: r }), providesTags: ["classSchedule", "team"] }),
    getStaffClassStudents: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/staff-dashboard/class-students", method: "POST", body: r }), providesTags: ["classSchedule"] }),
    storeStaffPlanner: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/staff-dashboard/planner", method: "POST", body: r }), invalidatesTags: ["team", "classSchedule"] }),
    submitStaffPlanner: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/staff-dashboard/planner/submit", method: "POST", body: r }), invalidatesTags: ["team", "classSchedule"] }),
    getStaffPlanners: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/staff-dashboard/planners", method: "POST", body: r }), providesTags: ["team"] }),
    getStaffTimetable: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/staff-dashboard/timetable", method: "POST", body: r }), providesTags: ["classSchedule", "team"] }),

    // Parent Portal
    getParentDashboard: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/parent-portal/dashboard", method: "POST", body: r }), providesTags: ["student", "payment"] }),
    getChildClasses: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/parent-portal/child-classes", method: "POST", body: JSON.stringify(r) }), providesTags: ["classSchedule"] }),
    getChildAttendanceSummary: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/parent-portal/child-attendance", method: "POST", body: JSON.stringify(r) }), providesTags: ["student"] }),
    // Phase-2 Attendance page (full detail: summary + filters + history).
    // Backend distinguishes "not_recorded" from "absent" — the UI must
    // preserve that distinction (spec §6).
    getChildAttendanceDetail: builder.query<IApiResponse, { studentId: number | null; from?: string; to?: string; subject_id?: number | null; status?: string }>({
      query: (r) => ({ url: "/parent-portal/child-attendance-detail", method: "POST", body: JSON.stringify(r) }),
      providesTags: ["student", "attendanceRecord"],
    }),
    // Phase-2 Subjects page (child's enrolled subjects, grouped by
    // grade in Step Up canonical order, staff per subject derived from
    // active class assignments).
    getChildSubjects: builder.query<IApiResponse, { studentId: number | null }>({
      query: (r) => ({ url: "/parent-portal/child-subjects", method: "POST", body: JSON.stringify(r) }),
      providesTags: ["student", "subject"],
    }),
    // Phase-3 Assessments (parent-safe: only rows where the assessment
    // group is `published` or the legacy row is `marked`).
    getChildAssessments: builder.query<IApiResponse, { studentId: number | null; from?: string; to?: string; subject_id?: number | null; status?: string }>({
      query: (r) => ({ url: "/parent-portal/child-assessments", method: "POST", body: JSON.stringify(r) }),
      providesTags: ["student", "assessment"],
    }),
    // Phase-3 Performance (aggregated averages, per-subject breakdown,
    // monthly trend). Same visibility filter as assessments.
    getChildPerformance: builder.query<IApiResponse, { studentId: number | null; subject_id?: number | null; from?: string; to?: string }>({
      query: (r) => ({ url: "/parent-portal/child-performance", method: "POST", body: JSON.stringify(r) }),
      providesTags: ["student", "assessment", "attendanceRecord"],
    }),
    // Phase-4 Assignments list. Only assignments where isPublished=1 AND
    // the student is linked via assignment_students pivot or a
    // submission — never course-derived so drafts/inaccessible rows
    // cannot appear.
    getChildAssignments: builder.query<IApiResponse, { studentId: number | null; status?: string; subject_id?: number | null; period?: string; from?: string; to?: string }>({
      query: (r) => ({ url: "/parent-portal/child-assignments", method: "POST", body: JSON.stringify(r) }),
      providesTags: ["student", "assignment", "assignmentSubmission"],
    }),
    // Phase-4 Assignment detail (description, feedback, attachments).
    // Backend re-verifies (parent owns student) AND (assignment is in
    // that student's visible set) before returning anything.
    getChildAssignmentDetail: builder.query<IApiResponse, { studentId: number | null; assignmentId: number | null }>({
      query: (r) => ({ url: "/parent-portal/child-assignment-detail", method: "POST", body: JSON.stringify(r) }),
      providesTags: ["assignment"],
    }),
    // Report Cards — parent-scoped, published-only. Endpoint returns
    // an empty list until report-card storage exists in the backend.
    getChildReportCards: builder.query<IApiResponse, { studentId: number | null }>({
      query: (r) => ({ url: "/parent-portal/child-report-cards", method: "POST", body: JSON.stringify(r) }),
      providesTags: ["student"],
    }),
    // Phase-5 aggregate Dashboard for the ONE selected child. Composes
    // KPI + preview data from the same helpers the detail pages use so
    // dashboard numbers cannot drift from Attendance/Assessments/
    // Assignments pages. One request instead of six.
    getChildDashboard: builder.query<IApiResponse, { studentId: number | null }>({
      query: (r) => ({ url: "/parent-portal/child-dashboard", method: "POST", body: JSON.stringify(r) }),
      providesTags: ["student", "classSchedule", "attendanceRecord", "assessment", "assignment", "invoice", "message"],
    }),
    sendPasswordResetLink: builder.mutation<IApiResponse, { user_id: number; type?: string }>({ query: (r) => ({ url: "/password/send-reset-link", method: "POST", body: JSON.stringify(r) }) }),
    validateResetToken: builder.mutation<IApiResponse, { token: string; email: string }>({ query: (r) => ({ url: "/password/validate-token", method: "POST", body: JSON.stringify(r) }) }),
    resetPasswordPublic: builder.mutation<IApiResponse, { token: string; email: string; password: string; password_confirmation: string }>({ query: (r) => ({ url: "/password/reset", method: "POST", body: JSON.stringify(r) }) }),
    getFamilyInvoices: builder.query<IApiResponse, void>({ query: () => ({ url: "/parent-portal/family-invoices", method: "POST" }), providesTags: ["payment"] }),
    payFamilyInvoices: builder.mutation<IApiResponse, { invoice_ids: number[] }>({ query: (r) => ({ url: "/parent-portal/pay", method: "POST", body: JSON.stringify(r) }), invalidatesTags: ["payment"] }),

    // Finance / Ledger
    getFinanceOverview: builder.query<IApiResponse, { location_id?: number | "all" | null } | void>({ query: (r) => ({ url: "/finance/overview", method: "POST", body: JSON.stringify(r || {}) }), providesTags: ["payment", "invoice"] }),
    getFinanceAccounts: builder.query<IApiResponse, void>({ query: () => ({ url: "/finance/accounts", method: "POST" }), providesTags: ["payment"] }),
    getFinanceLocations: builder.query<IApiResponse, void>({ query: () => ({ url: "/finance/locations", method: "POST" }), providesTags: ["payment"] }),
    getFinanceTransactions: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/finance/transactions", method: "POST", body: JSON.stringify(r) }), providesTags: ["payment"] }),
    getFinanceExpenses: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/finance/expenses", method: "POST", body: JSON.stringify(r) }), providesTags: ["payment"] }),
    getExpenseCategories: builder.query<IApiResponse, void>({ query: () => ({ url: "/finance/expense-categories", method: "POST" }), providesTags: ["payment"] }),
    storeExpense: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/finance/expenses/store", method: "POST", body: JSON.stringify(r) }), invalidatesTags: ["payment"] }),
    getOutstandingReport: builder.query<IApiResponse, { location_id?: number | "all" | null } | void>({ query: (r) => ({ url: "/finance/reports/outstanding", method: "POST", body: JSON.stringify(r || {}) }), providesTags: ["payment", "invoice"] }),
    getPaymentReceipt: builder.query<IApiResponse, { payment_id: number }>({ query: (r) => ({ url: "/receipt", method: "POST", body: JSON.stringify(r) }) }),
    recordManualPayment: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/payment/manual", method: "POST", body: JSON.stringify(r) }), invalidatesTags: ["payment", "invoice", "student"] }),
    getCashFlowReport: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/finance/reports/cash-flow", method: "POST", body: JSON.stringify(r) }), providesTags: ["payment"] }),
    getRevenueReport: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/finance/reports/revenue", method: "POST", body: JSON.stringify(r) }), providesTags: ["payment"] }),
    reportAbsence: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/parent-portal/report-absence", method: "POST", body: r }), invalidatesTags: ["classSchedule", "student"] }),
    getAbsenceReports: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/parent-portal/absence-reports", method: "POST", body: r }), providesTags: ["student"] }),
    getParentPayments: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/parent-portal/payments", method: "POST", body: r }), providesTags: ["payment", "invoice"] }),
    getParentTimetable: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/parent-portal/timetable", method: "POST", body: r }), providesTags: ["classSchedule"] }),
    getParentPlanners: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/parent-portal/planners", method: "POST", body: r }), providesTags: ["team"] }),
    getParentMessages: builder.query<IApiResponse, any>({ query: (r) => ({ url: "/parent-portal/messages", method: "POST", body: r }), providesTags: ["message"] }),
    sendParentMessage: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/parent-portal/send-message", method: "POST", body: r }), invalidatesTags: ["message"] }),
    getParentMessageRecipients: builder.query<IApiResponse, { student_id: number }>({ query: (r) => ({ url: "/parent-portal/messages/eligible-recipients", method: "POST", body: r }) }),
    markParentMessageRead: builder.mutation<IApiResponse, { id: number }>({ query: (r) => ({ url: "/parent-portal/messages/mark-read", method: "POST", body: r }), invalidatesTags: ["message"] }),

    // Class Live / Detail
    getClassLiveDetail: builder.query<IApiResponse, { id: number }>({ query: (r) => ({ url: "/class-live/detail", method: "POST", body: JSON.stringify(r) }), providesTags: ["classSchedule"] }),
    startClassLive: builder.mutation<IApiResponse, { id: number }>({ query: (r) => ({ url: "/class-live/start", method: "POST", body: JSON.stringify(r) }), invalidatesTags: ["classSchedule"] }),
    endClassLive: builder.mutation<IApiResponse, { id: number; notes?: string }>({ query: (r) => ({ url: "/class-live/end", method: "POST", body: JSON.stringify(r) }), invalidatesTags: ["classSchedule"] }),
    createClassRequest: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/class-live/request", method: "POST", body: JSON.stringify(r) }), invalidatesTags: ["classSchedule"] }),
    handleClassRequest: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/class-live/request/handle", method: "POST", body: JSON.stringify(r) }), invalidatesTags: ["classSchedule"] }),
    // Phase-6.1 parent-portal makeup workflow.
    getMakeupEligibleStaff: builder.query<IApiResponse, { student_id: number; class_schedule_id?: number; class_occurrence_id?: number }>({
      query: (r) => ({ url: "/parent-portal/makeup/eligible-staff", method: "POST", body: JSON.stringify(r) }),
    }),
    checkMakeupAvailability: builder.mutation<IApiResponse, any>({
      query: (r) => ({ url: "/parent-portal/makeup/check-availability", method: "POST", body: JSON.stringify(r) }),
    }),
    respondToClassRequest: builder.mutation<IApiResponse, { id: number; action: "accept" | "request_another" }>({
      query: (r) => ({ url: "/parent-portal/class-request/respond", method: "POST", body: JSON.stringify(r) }),
      invalidatesTags: ["classSchedule"],
    }),
    proposeClassRequestAlternative: builder.mutation<IApiResponse, any>({
      query: (r) => ({ url: "/class-live/request/propose", method: "POST", body: JSON.stringify(r) }),
      invalidatesTags: ["classSchedule"],
    }),
    scheduleClassRequest: builder.mutation<IApiResponse, { id: number }>({
      query: (r) => ({ url: "/class-live/request/schedule", method: "POST", body: JSON.stringify(r) }),
      invalidatesTags: ["classSchedule", "class"],
    }),
    // Phase-6.2 central staff Parent Requests queue.
    listParentRequests: builder.query<IApiResponse, { page?: number; per_page?: number; request_type?: string; status?: string; student_id?: number; staff_id?: number; from?: string; to?: string; search?: string }>({
      query: (r) => ({ url: "/class-live/parent-requests", method: "POST", body: JSON.stringify(r) }),
      providesTags: ["classSchedule"],
    }),
    sendClassMessage: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/class-live/message", method: "POST", body: JSON.stringify(r) }), invalidatesTags: ["classSchedule"] }),
    markClassAttendance: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/class-live/attendance", method: "POST", body: JSON.stringify(r) }), invalidatesTags: ["classSchedule"] }),

    // Chat / Messaging
    getChatConversations: builder.query<IApiResponse, void>({ query: () => ({ url: "/chat/conversations", method: "POST" }), providesTags: ["message"], keepUnusedDataFor: 5 }),
    createChatConversation: builder.mutation<IApiResponse, any>({ query: (r) => ({ url: "/chat/conversations/create", method: "POST", body: JSON.stringify(r) }), invalidatesTags: ["message"] }),
    getChatMessages: builder.query<IApiResponse, { conversation_id: number; before_id?: number; limit?: number }>({ query: (r) => ({ url: "/chat/messages", method: "POST", body: JSON.stringify(r) }), providesTags: ["message"], keepUnusedDataFor: 5 }),
    sendChatMessage: builder.mutation<IApiResponse, { conversation_id: number; body: string; type?: string; reply_to_id?: number }>({ query: (r) => ({ url: "/chat/messages/send", method: "POST", body: JSON.stringify(r) }), invalidatesTags: ["message"] }),
    markChatAsRead: builder.mutation<IApiResponse, { conversation_id: number }>({ query: (r) => ({ url: "/chat/messages/read", method: "POST", body: JSON.stringify(r) }), invalidatesTags: ["message"] }),
    getChatContacts: builder.query<IApiResponse, void>({ query: () => ({ url: "/chat/contacts", method: "POST" }), providesTags: ["message"] }),
    getChatUnreadCount: builder.query<IApiResponse, void>({ query: () => ({ url: "/chat/unread-count", method: "POST" }), providesTags: ["message"], keepUnusedDataFor: 10 }),

    // Staff helpers
    checkStaffExists: builder.mutation<IApiResponse, { email?: string; phone?: string }>({
      query: (r) => ({ url: "/staff/check-exists", method: "POST", body: JSON.stringify(r) }),
    }),
    generateStaffId: builder.query<IApiResponse, void>({
      query: () => ({ url: "/staff/generate-id", method: "GET" }),
      keepUnusedDataFor: 0,
    }),
    generateStaffUsername: builder.query<IApiResponse, void>({
      query: () => ({ url: "/staff/generate-username", method: "GET" }),
      keepUnusedDataFor: 0,
    }),

    // ── Billing V2 (Phase B) ──
    // The v2 engine keeps billing type on the STUDENT (monthly | class_wise)
    // and default fee on the CLASS. These endpoints are the only places the
    // UI ever writes to student_billing_history / class_fee_history; direct
    // student PUTs no longer own billing_type_v2 once v2 is on.
    getStudentBillingCurrent: builder.query<IApiResponse, number | string>({
      query: (studentId) => ({ url: `/students/${studentId}/billing/current`, method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "student", id: `billing-${id}` } as any, "student"],
      keepUnusedDataFor: 0,
    }),
    getStudentBillingHistory: builder.query<IApiResponse, number | string>({
      query: (studentId) => ({ url: `/students/${studentId}/billing/history`, method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "student", id: `billing-hist-${id}` } as any, "student"],
      keepUnusedDataFor: 0,
    }),
    getClassFeeHistory: builder.query<IApiResponse, number | string>({
      query: (classId) => ({ url: `/classes/${classId}/fee-history`, method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "class", id: `fee-hist-${id}` } as any, "class"],
      keepUnusedDataFor: 0,
    }),
    previewAssignmentBilling: builder.mutation<
      IApiResponse,
      { student_id: number; class_id: number; on_date?: string | null }
    >({
      query: (body) => ({
        url: "/billing/preview-assignment",
        method: "POST",
        body: JSON.stringify(body),
      }),
    }),
    switchStudentBilling: builder.mutation<
      IApiResponse,
      {
        studentId: number | string;
        body: {
          billing_type: "monthly" | "class_wise";
          monthly_amount: number | null;
          tax_applicable: boolean;
          effective_from: string;
          change_reason?: string;
          confirmed: true;
        };
      }
    >({
      query: ({ studentId, body }) => ({
        url: `/students/${studentId}/billing/switch`,
        method: "POST",
        body: JSON.stringify(body),
      }),
      invalidatesTags: ["student", "class", "invoice"],
    }),
  }),
});

export const {
  useGetCustomerQuery,
  useGetUserQuery,
  useLoginMutation,
  useForgotFindAccountMutation,
  useForgotSendMutation,
  useForgotVerifyCodeMutation,
  useForgotResetPasswordMutation,
  useCreateSettingMutation,
  useGetSettingQuery,
  useCreateDepartmentMutation,
  useDeleteDepartmentMutation,
  useGetDepartmentQuery,
  useGetAllUserPrivilegesQuery,
  useCreateUserMutation,
  useGetUserAdminOnyQuery,
  useDeleteUserMutation,
  useChangeUserPasswordMutation,
  useUpdateUserPrivilegesMutation,
  useGetMissingPunchOutQuery,
  useCreateUserRoleMutation,
  useDeleteUserRoleMutation,
  useGetUserRoleQuery,
  useCreateUserPrivilegeMutation,
  useGetUserPrivilegeQuery,
  useDeleteUserPrivilegeMutation,
  useGetDashboardSecSectionOrderListQuery,
  useGetUserInfoOnyQuery,
  useCreateRestaurantMutation,
  useDeleteRestaurantMutation,
  useGetRestaurantQuery,
  useDeleteRestaurantScheduleMutation,
  useCreateDarkThemeColorMutation,
  useCreateLightThemeColorMutation,
  useGetAllDarkThemeColorQuery,
  useGetAllLightThemeColorQuery,
  useCreateLightThemeColorOderMutation,
  useCreateDarkThemeColorOrderMutation,

  useCreateTeamMutation,
  useDeleteTeamMutation,
  useUpdateTeamStatusMutation,
  useGetTeamQuery,
  useGetClassScheduleFilterOptionsQuery,
  useGetStaffPortalClassSessionsFilterOptionsQuery,
  useGetStaffDashboardStatsQuery,
  useGetStaffTeachingProfileQuery,
  useGetStaffCalendarQuery,
  useGetMyStaffCalendarQuery,
  useGetMyStudentsQuery,
  useGetMyTeachingAssignmentsQuery,
  useGetMyProfileQuery,
  useGetMyProfileEditOptionsQuery,
  useUpdateMyProfileMutation,
  useStoreMyClassMutation,
  useGetMyStudentDetailQuery,
  useGetMyStudentCalendarQuery,
  useGetMyAssessmentsQuery, useGetMyAssessmentOptionsQuery, useCreateMyAssessmentMutation,
  useGetMyMessagesQuery, useGetMyMessageThreadQuery, useGetMyMessageRecipientsQuery, useSendMyMessageMutation, useMarkMyMessageReadMutation,
  useGetMyAdminMessageRecipientsQuery, useSendMyAdminMessageMutation,
  useGetMyMessageTemplatesQuery, useGetMyActiveMessageTemplatesQuery, useCreateMyMessageTemplateMutation,
  useUpdateMyMessageTemplateMutation, useActivateMyMessageTemplateMutation, useDeactivateMyMessageTemplateMutation, useResetMyMessageTemplateMutation, useDeleteMyMessageTemplateMutation,
  useGetAttendanceMessageTemplatesQuery,
  useGetAdminMessageTemplatesQuery, useUpdateAdminMessageTemplateMutation,
  useActivateAdminMessageTemplateMutation, useDeactivateAdminMessageTemplateMutation, useResetAdminMessageTemplateMutation,
  useGetMyParentRequestsQuery, useGetMyParentRequestDetailQuery, useHandleMyParentRequestMutation,
  useGetMyAssessmentDetailQuery,
  useUpdateMyAssessmentMutation,
  useGetMyStudentReportCardQuery,
  useGetMyAssignmentsQuery,
  useGetMyAssignmentDetailQuery,
  useGetStaffClassSchedulesQuery,
  useGetAllClassSchedulesQuery,
  useGetClassScheduleEligibleStudentsQuery,
  useGetClassScheduleEligibleStaffQuery,
  useGetClassSchedulePatternReplacementStaffMutation,
  useAddClassScheduleStudentMutation,
  useReleaseClassScheduleStudentMutation,
  useSwitchClassScheduleStaffMutation,
  useGetClassScheduleDetailsQuery,
  useUpdateClassScheduleTemplateMutation,
  useGetClassDetailQuery,
  useGetClassEligibleStudentsQuery,
  useGetPunchDetailsQuery,
  useGetPunchDashboardQuery,
  useDeleteEmployeeScheduleMutation,
  useGetEmployeeScheduleDetailsQuery,
  useGetEmployeeScheduleDetailsByDateQuery,
  useGetEmployeeImgQuery,
  useCreatePositionMutation,
  useDeletePositionMutation,
  useGetPositionQuery,
  useGetEmployeePayRollQuery,
  useCreateAdminEditPunchMutation,
  useGetEmployeePayRollAdminQuery,
  useGetlastUpdateDatePayrollQuery,
  useCreateRescheduleMutation,
  useDeleteRescheduleMutation,
  useGetRescheduleQuery,
  useGetTeamSerchOnlyQuery,
  useGetEmployeeScheduleDetailsByDateOnlyQuery,
  useRemovedRescheduleMutation,
  useGetRescheduleOrderByDateQuery,
  useGetEmployeePayRollAdminUseMutation,
  useGetAnEmployeePayRollAdminQuery,
  useGetAllPayrollListQuery,
  useGetAllPayrollRecordeDateRangeQuery,
  useGetSaveEmployeePayrollRecordQuery,
  useGetSaveAnEmployeePayRollAdminQuery,
  useGetSaveAnEmplEachRecordPayRollAdminQuery,
  useGetPunchDetailsEmpQuery,
  useGetSaveEmployeePayrollAnEmployeeRecordQuery,
  usePayrollRecordeEmpQuery,
  useEditEmployeePayRollAdminQuery,
  useGetDayRecordAnEmplEachRecordQuery,
  useEmpPayrollPayrollRecordReportQuery,
  useEditPunchInTimeMutation,
  useEditPunchOutTimeMutation,
  useCreateRescheduleUpdatedForAllDateMutation,
  useGetEmpScheduleDetailsByDateRangeQuery,
  useCreatefixedPayrollGenerateMutation,
  useGetEmployeePayRollAdminUseEditMutation,
  useGetDashboardFristSectionQuery,
  useGetDashboardSecSectionQuery,
  useCreateEmailMutation,
  useGetEmailQuery,
  useDeleteEmailMutation,
  useCreateEmailTemplatesMutation,
  useGetEmailTemplatesQuery,
  useDeleteEmailTemplatesMutation,
  useCreateEmailRecordMutation,
  useDeleteEmailRecordMutation,
  useGetEmailRecordQuery,
  useCreateUserStaffMutation,
  useDeleteUserStaffMutation,
  useDeleteAdminEditPunchMutation,
  useGetEmployeeScheduleDetailsByMonthQuery,
  useGetCheckAttananceStatusQuery,
  useGetEmpScheduleDetailsByDateDeleteRecordQuery,
  useGetOutOfLocationQuery,
  useGetAllStaffDhashboardFristQuery,
  useGetAttananceStaffDhashboardQuery,
  useGetPayrollStaffDhashboardQuery,
  useGetAttananceMainDhashboardQuery,
  useGetPayrollStaffMainDhashboardQuery,
  useResentSmsStaffMutation,
  useGetSmsRecordQuery,
  useCreateStaffSmsRecordMutation,
  useGetRequestQuery,
  useCreateRequestMutation,
  useDeleteRequestMutation,
  useResentEmailStaffMutation,
  useGetEmployeeScheduleDetailsByDateOnlyEmploeeQuery,
  useGetResponedentQuery,
  useCreateRespontantMutation,
  useDeleteRespontantMutation,
  useGetRequestNotificationQuery,
  useGetRequestDrawerDataQuery,
  useCreateRespontantUserMutation,
  useGetMonthActiveRequestQuery,
  useUpdateDynamicScheduleMutation,
  useGetMonthlyWorkDaysStaffDhashboardQuery,
  useGetEmployeeScheduleDetailsByMonthforEmpQuery,
  useGetPayrollDhashboardDataQuery,
  useGetAllDhashboardFristQuery,
  useCreateStudentMutation,
  useUpdateStudentBillingSetupMutation,
  useDeleteStudentMutation,
  useGetallStudentQuery,
  useGetStudentActivityQuery,
  useGetStudentWeeklyScheduleQuery,
  useGetStudentCalendarQuery,
  useGetStudentOccurrenceDetailsQuery,
  useLazyGetallStudentQuery,
  useCreateStudentperentMutation,
  useDeleteStudentperentMutation,
  useGetallStudentperentQuery,
  useLookupStudentperentQuery,
  useGetAllParentsQuery,
  useShowParentQuery,
  useGetParentInvoicesQuery,
  useCreateParentMutation,
  useUpdateParentMutation,
  useDeleteParentMutation,
  useToggleParentChildAccessMutation,
  useToggleParentAccessMutation,
  useRemoveParentChildMutation,
  useGenerateParentCredentialsMutation,
  useEnableParentPortalMutation,
  useGetParentPortalAccountsQuery,
  useGetDashboardOperationsQuery,
  useLazyGetDashboardClassRosterQuery,
  useGetDashboardFinanceSummaryQuery,
  useResetParentPasswordMutation,
  useSendParentLoginDetailsMutation,
  useSendParentResetLinkMutation,
  useCreateGradeMutation,
  useDeleteGradeMutation,
  useGetallGradeQuery,
  useGetsearchStudentperentQuery,
  useGetallEnumQuery,
  useGetallEnumSearchQuery,
  useCreateEnumMutation,
  useDeleteEnumMutation,
  useEnumFindOrCreateMutation,
  useGetallLocationQuery,
  useCreateLocationMutation,
  useDeleteLocationMutation,
  useGetallSecoundGradeQuery,
  useCreateSubjectMutation,
  useReorderSubjectsMutation,
  useCreateSubjectWithGradesMutation,
  useGetAssessmentFocusAreasQuery,
  useCreateAssessmentFocusAreaMutation,
  useUpdateAssessmentFocusAreaMutation,
  useDeleteAssessmentFocusAreaMutation,
  useReorderAssessmentFocusAreasMutation,
  useGetallSubjectQuery,
  useDeleteSubjectMutation,
  useCreateSubjectAddGradeMutation,
  useCreateSubjectRemovedGradeMutation,
  useGetAllGradeCountQuery,
  useCreateSubjectGradeAssignStaddMutation,
  useGetStaffSubjectAssignmentsQuery,
  useSaveStaffSubjectAssignmentsMutation,
  useAddStaffTeachingSubjectMutation,
  useGetStaffGradesAssignStaffQuery,
  useGetGradesAssignStaffQuery,
  useGetsearchSatffQuery,
  useGetStaffGradesAssignStaffGetPerentQuery,
  useAssignClassesCreateMutation,
  useGetAssignClassGetAllCalederQuery,
  useGetallAssignClassesQuery,
  useGetAssignClassEmpGetAllCalederQuery,
  useCreateTeamImageMutation,
  useCreateStudentImageMutation,
  useGetGradesAssignStudentQuery,
  useCreateSubjectGradeAssignStudentAddMutation,
  useReleaseBlockingClassesForSubjectMutation,
  useListBlockingClassesForSubjectQuery,
  useGetStaffGradesAssignStudentQuery,
  useCreateEmailStudentRecordMutation,
  useCreateStudentSmsRecordMutation,
  useResentEmailStudentMutation,
  useResentSmsStudentMutation,
  useGetParentEmailRecordsQuery,
  useCreateParentEmailRecordMutation,
  useResentEmailParentMutation,
  useGetParentSmsRecordsQuery,
  useCreateParentSmsRecordMutation,
  useResentSmsParentMutation,
  useResetStudentperentMutation,
  useSwitchPrimaryParentMutation,
  useDropStudentMutation,
  useReactivateStudentMutation,
  useToggleStaffPortalAccessMutation,
  useGetStaffPortalAccessQuery,
  useCreateStaffPortalAccountMutation,
  useSendStaffPortalInvitationMutation,
  useSendStaffPasswordResetMutation,
  useSuspendStaffPortalAccessMutation,
  useEnableStaffPortalAccessMutation,
  useGetParentPortalStatusQuery,
  useToggleParentPortalAccessMutation,
  useGetChatConversationsQuery,
  useCreateChatConversationMutation,
  useGetChatMessagesQuery,
  useSendChatMessageMutation,
  useMarkChatAsReadMutation,
  useGetChatContactsQuery,
  useGetChatUnreadCountQuery,
  useCreateGradeChanegNameMutation,
  useCreateSubjectChangeNameMutation,
  useGetAssignClassFilterQuery,
  useAssignClassStudentMutation,
  useAssignClassStudentMultiMutation,
  usePreviewAssignClassStudentBillingMutation,
  useAssignClassStudentWithBillingMutation,
  useGetStudentClassBillingQuery,
  useUpdateStudentClassBillingMutation,
  useGetAssignClassStudentGetAllCalederQuery,
  useGetAssignClassListFilterQuery,
  useDeleteClassfilterStudentMutation,
  useGetAssignClassHistoriesQuery,
  useDeleteAssignClassMutation,
  useAssignClassesUpdateMutation,
  useCreateStudentperentOnlyMutation,
  useCheckParentDuplicateMutation,
  useLinkExistingParentMutation,
  useReplaceParentLinkMutation,
  useRemoveParentFromStudentMutation,
  useGetParentLinkedStudentsQuery,
  useChangeUserPasswordSystemMutation,
  useGetBackgroundImgandLogoQuery,
  useGetallStudentByClassQuery,
  useGetClassSubjectByStaffQuery,
  useGetStaffGetAllGradeBySubjectQuery,
  useGetStudentSubjectByClassQuery,
  useGetStaffGradesAssignStudentByGradeQuery,
  useGetStudentByClassQuery,
  useGetClassScheduleQuery,
  useGetClassScheduleSummaryQuery,
  useGetOneClassScheduleQuery,
  useGetScheduleDetailsQuery,
  useGetOccurrenceEditFormQuery,
  useCreateClassScheduleMutation,
  useUpdateClassScheduleMutation,
  useSeriesEditClassScheduleMutation,
  useGenerateClassSchedulesMutation,
  useCheckClassScheduleConflictsMutation,
  useEnsureClassOccurrenceMutation,
  useDeleteClassScheduleMutation,
  useSwitchClassStaffMutation,
  useCheckStaffAvailabilityMutation,
  useBulkSwitchClassStaffMutation,
  useCheckBulkStaffAvailabilityMutation,
  useDuplicateClassMutation,
  useAssignStudentToClassMutation,
  useReleaseClassMutation,
  useRemoveStudentFromOccurrenceMutation,
  useGetMakeupOptionsMutation,
  useScheduleMakeupMutation,
  useGetPendingMakeupsQuery,
  useLazyGetPendingMakeupsQuery,
  useMarkMakeupNotRequiredMutation,
  useGetReplacementStaffMutation,
  useGetClassScheduleAvailableStaffMutation,
  useGetClassCancellationQuery,
  useCreateClassCancellationMutation,
  useGetAttendanceRecordQuery,
  useCreateAttendanceRecordMutation,
  useGetStudentAttendanceQuery,
  useGetClassAttendanceSummaryQuery,
  useGetAttendanceReportQuery,
  useStartAttendanceSessionMutation,
  useGetAttendanceSessionStudentsQuery,
  useSubmitAttendanceSessionMutation,
  useLateArrivalAttendanceSessionMutation,
  useGetLateStartsTodayQuery,
  useGetAdminAlertsTodayQuery,
  useGetClassNextSessionQuery,
  useGetStaffTodaySessionMutation,
  useGetClassSessionMessagesQuery,
  useSaveAttendanceSessionNoteMutation,
  useGetStudentAttendanceHistoryQuery,
  useGetAttendanceSessionsForDateQuery,
  useGetAttendanceReportListQuery,
  useGetAttendanceSessionRosterQuery,
  useGetAssessmentsQuery,
  useGetAssessmentStatsQuery,
  useGetAssessmentAnalyticsQuery,
  useGetStudentDashboardQuery,
  useGetAssessmentFocusesQuery,
  useCreateAssessmentMutation,
  useGetAssessmentCascadeGradesQuery,
  useLazyGetAssessmentCascadeGradesQuery,
  useGetAssessmentCascadeSubjectsQuery,
  useLazyGetAssessmentCascadeSubjectsQuery,
  useGetAssessmentCascadeStudentsQuery,
  useLazyGetAssessmentCascadeStudentsQuery,
  useCreateAssessmentGroupMutation,
  usePublishAssessmentGroupMutation,
  useGetAssessmentGroupRosterQuery,
  useLazyGetAssessmentGroupRosterQuery,
  useBulkGradeAssessmentGroupMutation,
  useUpdateAssessmentMutation,
  useDeleteAssessmentMutation,
  useSendClassSessionMessageMutation,
  useGetCourseQuery,
  useCreateCourseMutation,
  useDeleteCourseMutation,
  useGetCourseMaterialQuery,
  useCreateCourseMaterialMutation,
  useUploadCourseMaterialMutation,
  useDeleteCourseMaterialMutation,
  useReorderCourseMaterialMutation,
  useGetAssignmentQuery,
  useCreateAssignmentMutation,
  useCreateAssignmentAssignedToStudentsMutation,
  useDeleteAssignmentMutation,
  useGetAssignmentSubmissionQuery,
  useCreateAssignmentSubmissionMutation,
  useGradeAssignmentSubmissionMutation,
  useGetPaymentMethodQuery,
  useCreatePaymentMethodMutation,
  useDeletePaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
  useGetInvoiceQuery,
  useCreateInvoiceMutation,
  useDeleteInvoiceMutation,
  useSendInvoiceMutation,
  useGetOverdueInvoicesQuery,
  useGetInvoiceSummaryQuery,
  useGetInvoiceDetailQuery,
  useVoidInvoiceMutation,
  useDuplicateInvoiceMutation,
  useMarkInvoiceSentMutation,
  useEmailReceiptMutation,
  useCreateInvoiceItemMutation,
  useDeleteInvoiceItemMutation,
  useGetPaymentQuery,
  useCreatePaymentMutation,
  useGetStudentPaymentHistoryQuery,
  useGetPaymentSummaryQuery,
  useGetNotificationQuery,
  useMarkNotificationAsReadMutation,
  useGetUnreadNotificationCountQuery,
  useGetMessageQuery,
  useCreateMessageMutation,
  useGetMessageThreadQuery,
  useMarkMessageAsReadMutation,
  useDeleteMessageMutation,
  useGetActivityLogQuery,
  useGetPermissionMatrixQuery,
  useGetPermissionMatrixModulesQuery,
  useCreatePermissionMatrixMutation,
  useDeletePermissionMatrixMutation,
  useGetRolePermissionQuery,
  useUpdateRolePermissionMutation,
  useGetStaffAvailabilityQuery,
  useCreateStaffAvailabilityMutation,
  useDeleteStaffAvailabilityMutation,
  useGetAvailableStaffQuery,
  useGetStudentNoteQuery,
  useCreateStudentNoteMutation,
  useDeleteStudentNoteMutation,
  useToggleStudentNotePinMutation,
  useGetStudentHealthNotesQuery,
  useGetParentHelpArticlesQuery,
  useGetParentHelpArticleQuery,
  useGetProgramFolderQuery, useCreateProgramFolderMutation, useDeleteProgramFolderMutation, useMoveProgramToFolderMutation, useMoveFolderMutation,
  useGetTreatmentAreasQuery, useSaveTreatmentAreaMutation, useDeleteTreatmentAreaMutation, useReorderTreatmentAreasMutation,
  useGetMeasurementDefinitionsQuery, useSaveMeasurementDefinitionMutation,
  useGetProgramQuery, useCreateProgramMutation, useDeleteProgramMutation, useUpdateProgramStatusMutation, useCloneProgramMutation, useCopyProgramToClientsMutation, useLockProgramMutation, useUnlockProgramMutation, useProgramStudentSearchQuery, useLazyProgramStudentSearchQuery, useGetProgramByStudentQuery, useGetProgramLibraryMetricsQuery,
  useGetProgramStudentsQuery, useGetProgramProgressQuery, useGetProgramHistoryQuery,
  useGetSessionDataListQuery, useGetSessionDataKpisQuery,
  useGetProgramTargetQuery, useCreateProgramTargetMutation, useBulkImportTargetsMutation, useDuplicateProgramTargetMutation, useReorderProgramTargetsMutation, useUpdateTargetSDMutation, useUpdateTargetStatusMutation, useBulkTargetStatusMutation, useBulkTargetDeleteMutation, useDeleteProgramTargetMutation,
  useGetPromptTemplateQuery, useCreatePromptTemplateMutation, useDeletePromptTemplateMutation,
  useListPromptTemplatesQuery, useGetPromptTemplateByIdQuery, useUpdatePromptTemplateMutation,
  useDuplicatePromptTemplateMutation, useSetDefaultPromptTemplateMutation,
  useArchivePromptTemplateMutation, useRestorePromptTemplateMutation, useReorderPromptLevelsMutation,
  useGetMasteryWorkflowQuery, useCreateMasteryWorkflowMutation, useDeleteMasteryWorkflowMutation,
  useListMasteryWorkflowsQuery, useGetMasteryWorkflowByIdQuery, useUpdateMasteryWorkflowMutation,
  useDuplicateMasteryWorkflowMutation, useSetDefaultMasteryWorkflowMutation,
  useArchiveMasteryWorkflowMutation, useRestoreMasteryWorkflowMutation, useReorderMasteryPhasesMutation,
  useListPhaseIntervalsQuery, useCreatePhaseIntervalMutation, useUpdatePhaseIntervalMutation,
  useReorderPhaseIntervalsMutation, useDeletePhaseIntervalMutation,
  useListMasteryApprovalsQuery, useApproveMasteryApprovalMutation, useRejectMasteryApprovalMutation,
  useListProgramPromptLevelCustomizationsQuery, useSaveProgramPromptLevelCustomizationsMutation,
  useGetSessionDataQuery, useCreateSessionDataMutation, useGetTargetProgressQuery, useGetStudentOverviewQuery,
  useGetSessionByIdQuery, useLazyGetSessionByIdQuery,
  useGetSessionDraftCurrentQuery, useLazyGetSessionDraftCurrentQuery,
  useSessionAutosaveMutation, useSessionAddEntryMutation, useSessionUpdateEntryMutation,
  useSessionDeleteEntryMutation, useSessionUndoEntryMutation,
  useSessionCompleteMutation, useSessionSubmitReviewMutation, useSessionApproveMutation,
  useSessionReturnMutation, useSessionVoidMutation, useSessionRestoreMutation,
  useLazySearchSessionStudentsQuery, useLazySearchSessionStaffQuery,
  useLazySearchSessionProgramsQuery, useLazySearchSessionTargetsQuery,
  usePreviewTargetImportMutation, useCommitTargetImportMutation, useGetTodayAbaSessionsQuery,
  useListWorkflowObjectivesQuery, useCreateWorkflowObjectiveMutation,
  useCancelTodaySessionMutation, useEndProgramStudentAssignmentMutation,
  useBulkMoveProgramsMutation, useBulkStatusProgramsMutation, useBulkArchiveProgramsMutation, useBulkRestoreProgramsMutation,
  useSearchAbaStaffQuery, useLazySearchAbaStaffQuery,
  useListProgramAssignmentsQuery, useLazyListProgramAssignmentsQuery,
  useValidateProgramAssignmentsMutation, useSaveProgramAssignmentsMutation,
  useGetClientQuery, useCreateClientMutation, useGetClientProgramsQuery,
  useGetStudentAttendanceSummaryQuery,
  useGetStudentAttendanceCalendarQuery,
  useGetAttendanceSessionReportQuery,
  useGetStudentAssignmentsQuery, useGetStudentAssessmentListQuery, useGetStudentPaymentsQuery, useGetStudentPerformanceQuery, useGetStudentChallengesQuery,
  useStoreStudentAssessmentMutation, useStoreStudentChallengeMutation, useStoreStudentStrengthMutation,
  useDeleteStudentAssessmentMutation, useDeleteStudentChallengeMutation, useDeleteStudentStrengthMutation,
  useGetStaffDashboardQuery, useGetStaffClassRecordsQuery, useGetStaffClassStudentsQuery,
  useStoreStaffPlannerMutation, useSubmitStaffPlannerMutation, useGetStaffPlannersQuery, useGetStaffTimetableQuery, useGetTimetableWeekQuery,
  useGetParentDashboardQuery, useGetChildClassesQuery, useGetChildAttendanceSummaryQuery, useGetChildAttendanceDetailQuery, useGetChildSubjectsQuery, useGetChildAssessmentsQuery, useGetChildPerformanceQuery, useGetChildAssignmentsQuery, useGetChildAssignmentDetailQuery, useGetChildReportCardsQuery, useGetChildDashboardQuery, useGetFamilyInvoicesQuery, usePayFamilyInvoicesMutation,
  useSendPasswordResetLinkMutation, useValidateResetTokenMutation, useResetPasswordPublicMutation,
  useReportAbsenceMutation, useGetAbsenceReportsQuery,
  useGetFinanceOverviewQuery, useGetFinanceAccountsQuery, useGetFinanceLocationsQuery, useGetFinanceTransactionsQuery,
  useGetFinanceExpensesQuery, useGetExpenseCategoriesQuery, useStoreExpenseMutation,
  useGetOutstandingReportQuery, useGetCashFlowReportQuery, useGetRevenueReportQuery,
  useRecordManualPaymentMutation,
  useGetParentPaymentsQuery, useGetParentTimetableQuery, useGetParentPlannersQuery, useGetParentMessagesQuery, useSendParentMessageMutation,
  useGetParentMessageRecipientsQuery, useLazyGetParentMessageRecipientsQuery, useMarkParentMessageReadMutation,
  useCheckStaffExistsMutation, useGenerateStaffIdQuery, useGenerateStaffUsernameQuery,
  useGetClassLiveDetailQuery, useStartClassLiveMutation, useEndClassLiveMutation,
  useCreateClassRequestMutation, useHandleClassRequestMutation,
  useGetMakeupEligibleStaffQuery, useLazyGetMakeupEligibleStaffQuery,
  useCheckMakeupAvailabilityMutation,
  useRespondToClassRequestMutation,
  useProposeClassRequestAlternativeMutation,
  useScheduleClassRequestMutation,
  useListParentRequestsQuery,
  useSendClassMessageMutation, useMarkClassAttendanceMutation,
  // Billing V2
  useGetStudentBillingCurrentQuery, useLazyGetStudentBillingCurrentQuery,
  useGetStudentBillingHistoryQuery,
  useGetClassFeeHistoryQuery, useLazyGetClassFeeHistoryQuery,
  usePreviewAssignmentBillingMutation,
  useSwitchStudentBillingMutation,
} = attoDeskApi;
